import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Header from './Header'; // import your new Header component
import AuthConsentGate, { requiresAuthConsent } from './AuthConsentGate';
import useBaseLocations from './useBaseLocations';
import MapTab from "./MapTab";
import EditableCell from './EditableCell';
import './modernized_app.css';   // ← pull in stylesheet
import ATOscreen from './ATOscreen';
import LaunchAdvisorTab from './LaunchAdvisorTab';
import ChangeLogTab from './ChangeLogTab';
import PrioritiesTab from './PrioritiesTab';
import IssuesLog from './IssuesLog';
import ReportsTab from './ReportsTab';
import SafetyFormsTab from './SafetyFormsTab';
import StatusTab from './StatusTab';
import CrystalBallTab from './CrystalBallTab';
import SupplyTab from './SupplyTab';
import LostToolTab from './LostToolTab';
import ACFTPlansTab from './ACFTPlansTab';
import CalendarTab from './CalendarTab';
import WeatherTab from './WeatherTab';
import HelpTab from './HelpTab';
import AdminTab from './AdminTab';
import MFEPlatformOwnerPortal from './MFEPlatformOwnerPortal';
import UpgradeTab from './UpgradeTab';
import CommercialApp from './commercial/CommercialApp';
import CommercialRegistrationFields from './commercial/CommercialRegistrationFields';
import { MILITARY_ROLE_OPTIONS, MILITARY_SHOP_OPTIONS, PERSONNEL_LABOR_CATEGORY_OPTIONS, PERSONNEL_RANK_OPTIONS, PERSONNEL_SKILL_LEVEL_OPTIONS, PERSONNEL_SQUADRON_OPTIONS } from './militaryOptions';
import {
  compareTableValues,
  getNextSortConfig,
  getSortIndicator,
  getUniqueOptions,
  matchesColumnFilter,
  matchesDateFilter,
  matchesSelectFilter,
  matchesTriStateFilter,
} from './tableUtils';
import { ClearableInput, FilterToolbar } from './FilterControls';
import { CalendarClock, Pencil, Trash2, X } from 'lucide-react';
import { getOnboardingCalloutLayout } from './onboardingTourLayout';


const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api",
  withCredentials: true
});
const USER_ACTIVITY_SESSION_REFRESH_MS = 60 * 1000;
let lastUserActivityAt = Date.now();

function markUserActivityForSession() {
  lastUserActivityAt = Date.now();
}

function hasRecentUserActivityForSession() {
  return Date.now() - lastUserActivityAt <= USER_ACTIVITY_SESSION_REFRESH_MS;
}

api.interceptors.request.use(config => {
  if (hasRecentUserActivityForSession()) {
    config.headers = config.headers || {};
    config.headers['X-MFE-User-Active'] = '1';
  }
  return config;
});

const CAC_API_BASE_URL =
  process.env.REACT_APP_CAC_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000/api";
const CAC_WRONG_CERTIFICATE_HINT = 'Wrong certificate selected? Use Re-link CAC / PIV from Profile or retry in a private browser window.';
const PASSWORD_REQUIREMENTS_MESSAGE = 'Password must be at least 12 characters and include uppercase, lowercase, number, and symbol. It cannot contain the email name before @.';
const AFRC_HEADQUARTERS_LOCATION = 'Air Force Reserve Command';

function isAfRcHeadquartersLocation(value) {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  return Boolean(normalized) && (
    normalized === AFRC_HEADQUARTERS_LOCATION.toLowerCase()
    || normalized.endsWith(`- ${AFRC_HEADQUARTERS_LOCATION.toLowerCase()}`)
    || normalized.includes(AFRC_HEADQUARTERS_LOCATION.toLowerCase())
  );
}
const COMMAND_ANALYST_ROLE = 'Command Analyst';

function getPasswordStrengthError(candidatePassword, candidateEmail = '') {
  const value = String(candidatePassword || '');
  if (value.length < 12) return PASSWORD_REQUIREMENTS_MESSAGE;
  if (!/[A-Z]/.test(value)) return PASSWORD_REQUIREMENTS_MESSAGE;
  if (!/[a-z]/.test(value)) return PASSWORD_REQUIREMENTS_MESSAGE;
  if (!/\d/.test(value)) return PASSWORD_REQUIREMENTS_MESSAGE;
  if (!/[^A-Za-z0-9]/.test(value)) return PASSWORD_REQUIREMENTS_MESSAGE;

  const emailLocal = String(candidateEmail || '').split('@')[0].trim().toLowerCase();
  if (emailLocal.length >= 3 && value.toLowerCase().includes(emailLocal)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  return '';
}

function readBuildFlag(value, defaultValue) {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultValue;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y', 'on', 'enabled'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'n', 'off', 'disabled'].includes(normalized)) return false;
  return defaultValue;
}

const APP_EDITION = (process.env.REACT_APP_APP_EDITION || 'military').trim().toLowerCase();
const BUILD_ENABLE_COMMERCIAL_REGISTRATION = readBuildFlag(
  process.env.REACT_APP_ENABLE_COMMERCIAL_REGISTRATION,
  false
);
const BUILD_ENABLE_MILITARY_REGISTRATION = readBuildFlag(
  process.env.REACT_APP_ENABLE_MILITARY_REGISTRATION,
  true
);

function appScopeKey(location, globalView, user) {
  if (user?.is_superuser && globalView) return 'global';
  return `site:${String(location || user?.location || '').trim()}`;
}

function appScopeLabel(location, globalView, user) {
  if (user?.is_superuser && globalView) return 'Global View';
  return String(location || user?.location || 'selected site').trim() || 'selected site';
}

function truthyRequestValue(value) {
  return ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());
}

function requestDataValue(data, key) {
  if (!data) return '';
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return data.get(key) || '';
  }
  if (data instanceof URLSearchParams) {
    return data.get(key) || '';
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed?.[key] || '';
    } catch (err) {
      return '';
    }
  }
  if (typeof data === 'object') {
    return data[key] || '';
  }
  return '';
}

function requestScopeKey(config) {
  const params = config?.params || {};
  if (truthyRequestValue(params.global) || truthyRequestValue(requestDataValue(config?.data, 'global'))) {
    return 'global';
  }
  const location = params.location || requestDataValue(config?.data, 'location');
  return location ? `site:${String(location).trim()}` : '';
}


function applyTimezoneInterceptor(client) {
  client.interceptors.request.use((config) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // For GET requests
  if (!config.params) config.params = {};
  config.params.timezone = timezone;

  // For POST requests
  if (config.method === 'post' && config.data && typeof config.data === 'object') {
    config.data.timezone = timezone;
  }

  return config;
});
}

//global interceptor to normalize time to local time to UTC in the DB
applyTimezoneInterceptor(api);


// helper to format an ISO timestamp as "YYYY-MM-DD HH:MM:SS" in 24-hour local time
function formatLocalTimestamp(isoString) {
  const dt = new Date(isoString);
  const pad = n => String(n).padStart(2, '0');
  const Y = dt.getFullYear();
  const M = pad(dt.getMonth() + 1);
  const D = pad(dt.getDate());
  const h = pad(dt.getHours());
  const m = pad(dt.getMinutes());
  const s = pad(dt.getSeconds());
  return `${Y}-${M}-${D} ${h}:${m}`; //:${s} to add back seconds
}

function validatePersonnelHoursValue(value) {
  const trimmed = String(value ?? '').trim();

  if (trimmed === '') {
    return { isValid: true, value: '' };
  }

  const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) {
    return {
      isValid: false,
      message: 'Use HH:MM-HH:MM in 24-hour time',
    };
  }

  const start = `${match[1]}:${match[2]}`;
  const end = `${match[3]}:${match[4]}`;
  if (start === end) {
    return {
      isValid: false,
      message: 'Start and end cannot match',
    };
  }

  return {
    isValid: true,
    value: `${start}-${end}`,
  };
}

const initialCommercialRegistration = {
  commercialRegistrationMode: 'create',
  companyName: '',
  companyDomain: '',
  restrictSignupToCompanyDomain: false,
  templateType: 'asset_maintenance',
  timezone: 'UTC',
  locale: 'en-US',
  inviteCode: '',
};

const CURRENT_TERMS_VERSION = '2026-05-17';
const CURRENT_TERMS_URL = `/legal/mfe-terms-eula-${CURRENT_TERMS_VERSION}.html`;
const DEMO_LOCATION_NAME = 'Tampa International Airport (Demo)';
const PERSONNEL_TEXT_COLUMNS = [
  "Last Name",
  "First Name",
  "Rank",
  "Emp Num",
  "Email",
  "Mobile Phone",
  "SMS Opt-In",
  "Role",
  "Base Code",
  "Squadron",
  "Shop Name",
  "Shop Code",
  "Shift",
  "Hours",
  "Day Off",
  "Skill Level",
  "Labor Category",
  "DAFSC"
];
const PERSONNEL_BOOLEAN_FIELDS = [
  "Xs",
  "Shop Lead?",
  "Launch?",
  "Recovery?",
  "Present?",
  "Eligible for Cross-Location Support?"
];
const PERSONNEL_REQUIRED_FIELD_KEYS = new Set(["Last Name", "First Name", "Emp Num"]);
const PERSONNEL_UNRELEASED_FIELD_KEYS = new Set(["Mobile Phone", "SMS Opt-In"]);
const PERSONNEL_DEFAULT_HIDDEN_FIELD_KEYS = new Set(["Eligible for Cross-Location Support?"]);
const PERSONNEL_FIELD_LABELS = {
  "Day Off": "Days Off",
};
const PERSONNEL_DAYS_OFF_OPTIONS = [
  { value: 'Sun', label: 'Sun', aliases: ['Sunday'] },
  { value: 'Mon', label: 'Mon', aliases: ['Monday'] },
  { value: 'Tue', label: 'Tue', aliases: ['Tues', 'Tuesday'] },
  { value: 'Wed', label: 'Wed', aliases: ['Weds', 'Wednesday'] },
  { value: 'Thu', label: 'Thu', aliases: ['Thur', 'Thurs', 'Thursday'] },
  { value: 'Fri', label: 'Fri', aliases: ['Friday'] },
  { value: 'Sat', label: 'Sat', aliases: ['Saturday'] },
];
const PERSONNEL_FIELD_COLUMNS = [
  ...PERSONNEL_TEXT_COLUMNS.map(key => ({ key, label: PERSONNEL_FIELD_LABELS[key] || key })),
  ...PERSONNEL_BOOLEAN_FIELDS.map(key => ({ key, label: key })),
  { key: "lastUploadedAt", label: "Last Uploaded" }
];

const DEFAULT_FLY_SCHEDULE_COLLAPSE_KEYS = [
  'callSign',
  'priority',
  'config',
  'fuelGoal',
  'missionType',
  'landingCode',
  'uploadedBy',
  'lastUploadedAt',
];
const DEFAULT_PERSONNEL_COLLAPSE_KEYS = [
  'Base Code',
  'Shop Code',
  'Shift',
];

function getDefaultPersonnelFieldColumns() {
  return PERSONNEL_FIELD_COLUMNS.map(column => ({
    ...column,
    required: PERSONNEL_REQUIRED_FIELD_KEYS.has(column.key),
    visible: !PERSONNEL_UNRELEASED_FIELD_KEYS.has(column.key) && !PERSONNEL_DEFAULT_HIDDEN_FIELD_KEYS.has(column.key),
  }));
}

function mergePersonnelFieldColumns(savedColumns) {
  const defaults = getDefaultPersonnelFieldColumns();
  if (!Array.isArray(savedColumns)) return defaults;
  const savedByKey = new Map(savedColumns.map(column => [column.key, column]));
  return defaults.map(column => {
    const saved = savedByKey.get(column.key);
    if (PERSONNEL_UNRELEASED_FIELD_KEYS.has(column.key)) {
      return {
        ...column,
        visible: false,
      };
    }
    return {
      ...column,
      visible: column.required ? true : saved ? saved.visible !== false : column.visible,
    };
  });
}

function mergePersonnelCollapseSettings(value) {
  const defaults = {
    collapsed: false,
    hiddenKeys: DEFAULT_PERSONNEL_COLLAPSE_KEYS,
  };
  if (!value || typeof value !== 'object') return defaults;
  const validKeys = new Set(PERSONNEL_FIELD_COLUMNS.map(column => column.key));
  const hiddenKeys = Array.isArray(value.hiddenKeys)
    ? value.hiddenKeys.filter(key => validKeys.has(key))
    : defaults.hiddenKeys;
  return {
    collapsed: Boolean(value.collapsed),
    hiddenKeys: hiddenKeys.length ? hiddenKeys : defaults.hiddenKeys,
  };
}

function PersonnelFieldSettings({ columns, onChangeColumns, onClose }) {
  const releasedColumns = columns.filter(column => !PERSONNEL_UNRELEASED_FIELD_KEYS.has(column.key));
  const toggleColumn = key => {
    onChangeColumns(columns.map(column => (
      column.key === key && !column.required
        ? { ...column, visible: !column.visible }
        : column
    )));
  };

  return (
    <div className="status-field-settings personnel-field-settings" role="dialog" aria-label="Personnel table field settings">
      <div className="status-field-settings__header">
        <strong>Personnel Fields</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which Personnel columns show in the table. Name and Man # stay visible so every row remains identifiable.</p>
      <div className="status-field-settings__list">
        {releasedColumns.map(column => (
          <label key={column.key} className={column.required ? 'is-required' : ''}>
            <input
              type="checkbox"
              checked={column.visible}
              disabled={column.required}
              onChange={() => toggleColumn(column.key)}
            />
            <span>{column.label}</span>
          </label>
        ))}
      </div>
      <button
        type="button"
        className="status-field-settings__reset"
        onClick={() => onChangeColumns(getDefaultPersonnelFieldColumns())}
      >
        Reset Fields
      </button>
    </div>
  );
}

function PersonnelCollapseSettings({ columns, hiddenKeys, onChangeHiddenKeys, onClose }) {
  const availableColumns = columns.filter(column => column.visible && !column.required && !PERSONNEL_UNRELEASED_FIELD_KEYS.has(column.key));
  const hiddenKeySet = new Set(hiddenKeys);
  const toggleHiddenKey = key => {
    onChangeHiddenKeys(prev => (
      prev.includes(key)
        ? prev.filter(existingKey => existingKey !== key)
        : [...prev, key]
    ));
  };

  return (
    <div className="status-field-settings personnel-field-settings" role="dialog" aria-label="Personnel collapse settings">
      <div className="status-field-settings__header">
        <strong>Collapse View</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which currently visible Personnel fields are temporarily hidden when the table is collapsed.</p>
      <div className="status-field-settings__list">
        {availableColumns.map(column => (
          <label key={column.key}>
            <input
              type="checkbox"
              checked={hiddenKeySet.has(column.key)}
              onChange={() => toggleHiddenKey(column.key)}
            />
            <span>{column.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_USER_PREFERENCES = {
  theme: 'dark',
  onboardingCompletedAt: '',
  onboardingSkippedAt: '',
  emailNotifications: {
    launchAssignments: true,
    recoveryAssignments: true,
    priorityAssignments: true,
    redballIssues: true,
  },
  smsNotifications: {
    assignmentAlerts: true,
    redballIssues: true,
    extraOperationalAlerts: false,
  },
  tableSettings: {
    flySchedule: {
      columns: [],
      collapse: {
        collapsed: false,
        hiddenKeys: DEFAULT_FLY_SCHEDULE_COLLAPSE_KEYS,
      },
    },
    personnel: {
      columns: [],
      collapse: {
        collapsed: false,
        hiddenKeys: DEFAULT_PERSONNEL_COLLAPSE_KEYS,
      },
    },
  },
};

const ONBOARDING_STEPS = [
  {
    title: 'Start With Fly Schedule',
    body: 'Use Fly Schedule as the daily execution board. Click the pencil icon on a flight row to record actual times, cancellations, assign spare aircraft, assign personnel, and more.',
    action: 'To edit scheduled times, click directly onto the time-cells in the row. The small clock icon means that actual time has not been entered yet.',
    targetTab: 'ato',
    targetLabel: 'Open Fly Schedule',
    targetSelector: '[data-tour-target="tab-fly-schedule"]',
  },
  {
    title: 'Assign Launch Personnel',
    body: 'Launch personnel are managed from the Fly Schedule. Use the Auto-Assign Launch button above the Launch Personnel column, or click the pencil icon to choose personnel manually.',
    action: 'Auto-assign tries to place two eligible people per aircraft. If only one is available, it assigns that person and warns that the row is understaffed.',
    targetTab: 'ato',
    targetLabel: 'Show Auto-Assign',
    targetSelector: '[data-tour-target="auto-assign-launch"]',
  },
  {
    title: 'Keep Aircraft Status Current',
    body: 'Use Status as the master source for fleet health, parking spot, current fuel, ETIC, and whether a jet is homestation or offstation.',
    action: 'If a tail has open issues, Status will show an issue indicator, ETIC, and changing the aircraft status even populates the parking-ramp map.',
    targetTab: 'status',
    targetLabel: 'Open Status',
    targetSelector: '[data-tour-target="tab-status"]',
  },
  {
    title: 'Use Personnel As The Roster Source',
    body: 'Use Personnel to manage shift hours, days off, launch and recovery eligibility, and whether someone is present.',
    action: 'Changing the toggle for Present to yes / no impacts availability for launch, recovery, and priority jobs. Role and shop for registered users are controlled in Admin.',
    targetTab: 'personnel',
    targetLabel: 'Open Personnel',
    targetSelector: '[data-tour-target="tab-personnel"]',
  },
  {
    title: 'Log Aircraft Issues',
    body: 'Use Issues Log to track aircraft discrepancies, redballs, ETICs, and other open issues against specific tails.',
    action: "Clicking a row in Fly Schedule pre-populates the Issue form. Redballs will generate alerts, feed the Status tab, and influence the Launch-Success Advisor's prediction.",
    targetTab: 'issuesLog',
    targetLabel: 'Open Issues Log',
    targetSelector: '[data-tour-target="tab-issues-log"]',
  },
  {
    title: 'Track Production Priorities',
    body: 'Use the Priorities page to assign planned AFSC-specific jobs, discrepancies, or follow-on actions.',
    action: 'Jobs can only be assigned to personnel marked as Present on the Personnel tab. Jobs will carryforward until marked complete',
    targetTab: 'priorities',
    targetLabel: 'Open Priorities',
    targetSelector: '[data-tour-target="tab-priorities"]',
  },
  {
    title: 'Use Reports',
    body: 'Use Reports for commander dashboards, AI briefs, safety intelligence, trends, and printable/exportable summaries.',
    action: 'Reports are where leaders can turn daily execution data into AI-powered actionable insights.',
    targetTab: 'reports',
    targetLabel: 'Open Reports',
    targetSelector: '[data-tour-target="tab-reports"]',
  },
  {
    title: 'Find Help And Replay This Tour',
    body: 'Use Help for role rules, workflow explanations, and other guidance. You can replay this guided tour later from the Help page.',
    action: 'Use the input form to send MFE your feedback and improvement ideas!',
    targetTab: 'help',
    targetLabel: 'Open Help',
    targetSelector: '[data-tour-target="tab-help"]',
  },
  {
    title: 'Set Profile Preferences',
    body: 'Use Profile settings to manage user preferences like email notifications and display options, and to link your profile for CAC-enabled login.',
    action: 'Profile settings are personal to each user, while Admin button settings affect the entire site.',
    targetLabel: 'Show Profile',
    targetSelector: '[data-tour-target="profile-button"]',
  },
];

function normalizeUserPreferences(value) {
  const raw = value && typeof value === 'object' ? value : {};
  const rawEmail = raw.emailNotifications && typeof raw.emailNotifications === 'object'
    ? raw.emailNotifications
    : {};
  const rawSms = raw.smsNotifications && typeof raw.smsNotifications === 'object'
    ? raw.smsNotifications
    : {};
  const rawTableSettings = raw.tableSettings && typeof raw.tableSettings === 'object'
    ? raw.tableSettings
    : {};
  const rawFlySchedule = rawTableSettings.flySchedule && typeof rawTableSettings.flySchedule === 'object'
    ? rawTableSettings.flySchedule
    : {};
  const rawFlyCollapse = rawFlySchedule.collapse && typeof rawFlySchedule.collapse === 'object'
    ? rawFlySchedule.collapse
    : {};
  const rawPersonnel = rawTableSettings.personnel && typeof rawTableSettings.personnel === 'object'
    ? rawTableSettings.personnel
    : {};
  const rawPersonnelCollapse = rawPersonnel.collapse && typeof rawPersonnel.collapse === 'object'
    ? rawPersonnel.collapse
    : {};
  const theme = ['system', 'light', 'dark'].includes(raw.theme) ? raw.theme : 'system';
  const flyScheduleColumns = Array.isArray(rawFlySchedule.columns) ? rawFlySchedule.columns : [];
  const personnelColumns = Array.isArray(rawPersonnel.columns) ? rawPersonnel.columns : [];
  const personnelCollapseHiddenKeys = Array.isArray(rawPersonnelCollapse.hiddenKeys)
    ? rawPersonnelCollapse.hiddenKeys.filter(key => String(key || '').trim())
    : DEFAULT_PERSONNEL_COLLAPSE_KEYS;
  const collapseHiddenKeys = Array.isArray(rawFlyCollapse.hiddenKeys)
    ? rawFlyCollapse.hiddenKeys.filter(key => String(key || '').trim())
    : DEFAULT_FLY_SCHEDULE_COLLAPSE_KEYS;
  return {
    theme,
    onboardingCompletedAt: typeof raw.onboardingCompletedAt === 'string' ? raw.onboardingCompletedAt : '',
    onboardingSkippedAt: typeof raw.onboardingSkippedAt === 'string' ? raw.onboardingSkippedAt : '',
    emailNotifications: {
      launchAssignments: rawEmail.launchAssignments !== false,
      recoveryAssignments: rawEmail.recoveryAssignments !== false,
      priorityAssignments: rawEmail.priorityAssignments !== false,
      redballIssues: rawEmail.redballIssues !== false,
    },
    smsNotifications: {
      assignmentAlerts: rawSms.assignmentAlerts !== false,
      redballIssues: rawSms.redballIssues !== false,
      extraOperationalAlerts: rawSms.extraOperationalAlerts === true,
    },
    tableSettings: {
      flySchedule: {
        columns: flyScheduleColumns,
        collapse: {
          collapsed: rawFlyCollapse.collapsed === true,
          hiddenKeys: collapseHiddenKeys.length ? collapseHiddenKeys : DEFAULT_FLY_SCHEDULE_COLLAPSE_KEYS,
        },
      },
      personnel: {
        columns: personnelColumns,
        collapse: {
          collapsed: rawPersonnelCollapse.collapsed === true,
          hiddenKeys: personnelCollapseHiddenKeys.length ? personnelCollapseHiddenKeys : DEFAULT_PERSONNEL_COLLAPSE_KEYS,
        },
      },
    },
  };
}

function canUserAccessOperationalData(user) {
  return Boolean(user && user.can_access_operational_data !== false);
}

function isCommercialRegistrationReady(form) {
  if (!form) return false;

  if (form.commercialRegistrationMode === 'join') {
    return Boolean((form.companyName || '').trim() || (form.inviteCode || '').trim());
  }

  return Boolean((form.companyName || '').trim() && form.templateType && form.timezone && form.locale);
}

function buildCommercialRegistrationPayload(form) {
  return {
    commercialRegistrationMode: form.commercialRegistrationMode,
    companyName: form.companyName,
    companyDomain: form.companyDomain,
    restrictSignupToCompanyDomain: form.restrictSignupToCompanyDomain,
    templateType: form.templateType,
    timezone: form.timezone,
    locale: form.locale,
    inviteCode: form.inviteCode,
  };
}

function getCommercialRegistrationAttemptKey(candidateEmail, form) {
  return JSON.stringify({
    email: (candidateEmail || '').trim().toLowerCase(),
    ...buildCommercialRegistrationPayload(form),
  });
}

function toDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function emptyAvailabilityPeriod() {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return {
    id: '',
    category: 'Leave',
    startAt: toDateTimeLocal(start),
    endAt: toDateTimeLocal(end),
    availabilityPercent: 0,
    notes: '',
  };
}

function PersonnelAvailabilityModal({ api: apiClient, user, selectedBase, person, onClose }) {
  const [periods, setPeriods] = useState([]);
  const [categories, setCategories] = useState([
    'Deployment', 'Post-deployment leave', 'Leave', 'Sick', 'TDY',
    'Appointment', 'Medical restriction', 'Training', 'Other',
  ]);
  const [draft, setDraft] = useState(emptyAvailabilityPeriod);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/personnel/${person.id}/availability-periods`, {
        params: { email: user.email, location: selectedBase },
      });
      setPeriods(response.data.periods || []);
      setCategories(response.data.categories || []);
      setMessage('');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Availability schedule could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, person.id, selectedBase, user.email]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const savePeriod = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      await apiClient.post(`/personnel/${person.id}/availability-periods`, {
        ...draft,
        email: user.email,
        location: selectedBase,
      });
      setDraft(emptyAvailabilityPeriod());
      await loadPeriods();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Availability period could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const editPeriod = period => setDraft({
    id: period.id,
    category: period.category,
    startAt: toDateTimeLocal(period.startAt),
    endAt: toDateTimeLocal(period.endAt),
    availabilityPercent: period.availabilityPercent,
    notes: period.notes || '',
  });

  const deletePeriod = async period => {
    if (!window.confirm(`Delete ${period.category} availability period?`)) return;
    try {
      await apiClient.delete(`/personnel/${person.id}/availability-periods/${period.id}`, {
        data: { email: user.email, location: selectedBase },
      });
      if (draft.id === period.id) setDraft(emptyAvailabilityPeriod());
      await loadPeriods();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Availability period could not be deleted.');
    }
  };

  return (
    <div className="personnel-availability-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="personnel-availability-modal" role="dialog" aria-modal="true" aria-labelledby="personnel-availability-title" onMouseDown={event => event.stopPropagation()}>
        <header>
          <div><span>Personnel planning</span><h2 id="personnel-availability-title">{person['Rank']} {person['First Name']} {person['Last Name']}</h2></div>
          <button type="button" onClick={onClose} title="Close availability schedule"><X size={18} /></button>
        </header>
        <div className="personnel-availability-modal__body">
          <form onSubmit={savePeriod}>
            <h3>{draft.id ? 'Edit availability exception' : 'Add availability exception'}</h3>
            <label><span>Type</span><select value={draft.category} onChange={event => setDraft({ ...draft, category: event.target.value })}>{categories.map(category => <option key={category} value={category}>{category}</option>)}</select></label>
            <label><span>Start</span><input type="datetime-local" value={draft.startAt} onChange={event => setDraft({ ...draft, startAt: event.target.value })} required /></label>
            <label><span>End</span><input type="datetime-local" value={draft.endAt} onChange={event => setDraft({ ...draft, endAt: event.target.value })} required /></label>
            <label><span>Capacity available</span><div className="personnel-availability-percent"><input type="number" min="0" max="100" step="5" value={draft.availabilityPercent} onChange={event => setDraft({ ...draft, availabilityPercent: event.target.value })} /><span>%</span></div></label>
            <label><span>Notes</span><textarea value={draft.notes} onChange={event => setDraft({ ...draft, notes: event.target.value })} placeholder="Optional details" /></label>
            <div className="personnel-availability-modal__actions">
              <button type="submit" disabled={saving}>{saving ? 'Saving...' : draft.id ? 'Update exception' : 'Add exception'}</button>
              {draft.id ? <button type="button" onClick={() => setDraft(emptyAvailabilityPeriod())}>Cancel edit</button> : null}
            </div>
          </form>
          <section className="personnel-availability-modal__schedule">
            <h3>Scheduled exceptions</h3>
            {message ? <p className="personnel-availability-modal__message">{message}</p> : null}
            {loading ? <p className="personnel-availability-modal__empty">Loading schedule...</p> : periods.length ? periods.map(period => (
              <article key={period.id}>
                <div><strong>{period.category}</strong><span>{new Date(period.startAt).toLocaleString()} to {new Date(period.endAt).toLocaleString()}</span><small>{period.availabilityPercent}% available{period.notes ? ` | ${period.notes}` : ''}</small></div>
                <div><button type="button" onClick={() => editPeriod(period)} title="Edit period"><Pencil size={16} /></button><button type="button" onClick={() => deletePeriod(period)} title="Delete period"><Trash2 size={16} /></button></div>
              </article>
            )) : <p className="personnel-availability-modal__empty">No future exceptions recorded.</p>}
          </section>
        </div>
      </section>
    </div>
  );
}

function App() {   
  // Global state for location filtering
  const [selectedBase, setSelectedBase] = useState('');
  const [globalView, setGlobalView] = useState(false);  
  const [siteLoadIndicator, setSiteLoadIndicator] = useState(null);
  const siteLoadIndicatorRef = useRef(null);
  const completedSiteScopeRef = useRef({ key: '', label: '' });
  const siteLoadClearTimerRef = useRef(null);
  const [featureFlags, setFeatureFlags] = useState({});
   
  // Authentication, data, and UI state variables
  const [userType,       setUserType]        = useState('');    // “Commercial” | “USAF” | “USArmy”
  const [isCodeSent,     setIsCodeSent]      = useState(false);
  const [isCodeVerified, setIsCodeVerified]  = useState(false);
  const [regCode,        setRegCode]         = useState('');    // 6‑digit pin  
 
  // for banner notifications like "saved successfully" on personnel screen
  const [notification, setNotification] = useState('');
  const notificationStyle = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    background: '#4caf50',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '4px',
    zIndex: 1100,
  };
  
  const [user, setUser] = useState(null);
  const {
    tree: baseLocationTree,
    coords: baseCoordMap,
    loading: baseLocationsLoading,
    error: baseLocationsError,
    reload: reloadBaseLocations,
  } = useBaseLocations(api, user?.email);
  const [isSessionRestorePending, setIsSessionRestorePending] = useState(true);
  const [headerSubscription, setHeaderSubscription] = useState(null);
  const [isHeaderSubscriptionLoading, setIsHeaderSubscriptionLoading] = useState(false);
  const [billingReturn, setBillingReturn] = useState(null);
  const [activeTab, setActiveTab] = useState("ato");
  const [personnelData, setPersonnelData] = useState([]);
  const [personnelDataScope, setPersonnelDataScope] = useState({ key: '', label: '' });
  const [personnelLoadingScope, setPersonnelLoadingScope] = useState(null);
  const personnelFetchRequestRef = useRef(0);
  const [personnelSortConfig, setPersonnelSortConfig] = useState({ key: "Last Name", direction: "asc" });
  const [personnelFieldColumns, setPersonnelFieldColumns] = useState(getDefaultPersonnelFieldColumns);
  const [showPersonnelFieldSettings, setShowPersonnelFieldSettings] = useState(false);
  const [personnelCollapseSettings, setPersonnelCollapseSettings] = useState(() => mergePersonnelCollapseSettings());
  const [showPersonnelCollapseSettings, setShowPersonnelCollapseSettings] = useState(false);
  const [activePersonnelAvailabilityId, setActivePersonnelAvailabilityId] = useState(null);
  const [personnelAvailabilityAnchorRect, setPersonnelAvailabilityAnchorRect] = useState(null);
  const [personnelAvailabilityPerson, setPersonnelAvailabilityPerson] = useState(null);
  const [personnelFilters, setPersonnelFilters] = useState({});
  const [milpersMappings, setMilpersMappings] = useState({
    locations: [],
    locationMappings: [],
    dafscMappings: [],
    unmappedPersonnelDafscs: [],
    shopOptions: MILITARY_SHOP_OPTIONS,
  });
  const [milpersSummary, setMilpersSummary] = useState(null);
  const [milpersMessage, setMilpersMessage] = useState('');
  const [milpersError, setMilpersError] = useState('');
  const [isMilpersLoading, setIsMilpersLoading] = useState(false);
  const [milpersUploadProgress, setMilpersUploadProgress] = useState(null);
  const [milpersProcessingPhase, setMilpersProcessingPhase] = useState('');
  const [milpersImportJob, setMilpersImportJob] = useState(null);
  const [milpersImportJobs, setMilpersImportJobs] = useState([]);
  const [showMilpersImportModal, setShowMilpersImportModal] = useState(false);
  const [milpersLocationDraft, setMilpersLocationDraft] = useState({ dutyLocationCode: '', location: '' });
  const [milpersDafscSelection, setMilpersDafscSelection] = useState([]);
  const [milpersManualDafsc, setMilpersManualDafsc] = useState('');
  const [milpersShopName, setMilpersShopName] = useState('');
  const milpersFileRef = useRef(null);
  const milpersJobPollRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authSecretClearNonce, setAuthSecretClearNonce] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [commercialRegistration, setCommercialRegistration] = useState(initialCommercialRegistration);
  const [lastCommercialCodeAttemptKey, setLastCommercialCodeAttemptKey] = useState('');
  const [commercialCodeNeedsResend, setCommercialCodeNeedsResend] = useState(false);
  //password reset option
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isCodeStep, setIsCodeStep] = useState(false);
  const [role, setRole] = useState('');
  const [shop, setShop] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [authConfig, setAuthConfig] = useState({
    authMode: 'local',
    passwordLoginEnabled: true,
    cacLoginEnabled: false,
    appEdition: APP_EDITION,
    commercialRegistrationEnabled: BUILD_ENABLE_COMMERCIAL_REGISTRATION,
    militaryRegistrationEnabled: BUILD_ENABLE_MILITARY_REGISTRATION,
    enabledMilitaryRegistrationTypes: ['USAF'],
  });
  const [isSendingRegistrationCode, setIsSendingRegistrationCode] = useState(false);
  const [registrationLocationAdminStatus, setRegistrationLocationAdminStatus] = useState(null);
  const [isLoadingRegistrationAdminStatus, setIsLoadingRegistrationAdminStatus] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(DEFAULT_USER_PREFERENCES);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [accountClosePassword, setAccountClosePassword] = useState('');
  const [accountCloseConfirm, setAccountCloseConfirm] = useState('');
  const [isClosingAccount, setIsClosingAccount] = useState(false);
  const [cacLinkPassword, setCacLinkPassword] = useState('');
  const [isLinkingCac, setIsLinkingCac] = useState(false);
  const [locationTransferRequests, setLocationTransferRequests] = useState([]);
  const [locationTransferDraft, setLocationTransferDraft] = useState({
    country: '',
    state: '',
    base: '',
    role: '',
    shop: '',
    reason: '',
  });
  const [isSubmittingLocationTransfer, setIsSubmittingLocationTransfer] = useState(false);
  const [siteAdminClaimStatus, setSiteAdminClaimStatus] = useState(null);
  const [isClaimingSiteAdmin, setIsClaimingSiteAdmin] = useState(false);
  const [locationSharingNotice, setLocationSharingNotice] = useState(null);
  const [locationSharingNoticeDismissed, setLocationSharingNoticeDismissed] = useState(false);
  const [locationSharingRetryNonce, setLocationSharingRetryNonce] = useState(0);
  const [weatherSummary, setWeatherSummary] = useState(null);
  const [dismissedWeatherBannerKey, setDismissedWeatherBannerKey] = useState('');
  const [platformAnnouncements, setPlatformAnnouncements] = useState([]);
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem('mfeDismissedAnnouncements') || '[]');
    } catch (err) {
      return [];
    }
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingDontShowAgain, setOnboardingDontShowAgain] = useState(false);
  const [onboardingTargetRect, setOnboardingTargetRect] = useState(null);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [base, setBase] = useState('');
  const canRequestLocationTransfer = Boolean(
    user?.email &&
    ['USAF', 'USArmy'].includes(user?.userType || '') &&
    !user?.is_superuser &&
    !user?.is_support_user
  );
  const normalizedUserPreferences = useMemo(
    () => normalizeUserPreferences(user?.preferences),
    [user?.preferences]
  );
  const weatherEnabled = Boolean(headerSubscription?.integrationFlags?.weather);
  const currentSiteScopeKey = useMemo(
    () => appScopeKey(selectedBase, globalView, user),
    [selectedBase, globalView, user]
  );
  const currentSiteScopeLabel = useMemo(
    () => appScopeLabel(selectedBase, globalView, user),
    [selectedBase, globalView, user]
  );

  const clearSiteLoadIndicator = useCallback((scopeKey, scopeLabel) => {
    const active = siteLoadIndicatorRef.current;
    if (scopeKey && active && active.targetKey !== scopeKey) return;
    const completedKey = scopeKey || active?.targetKey || currentSiteScopeKey;
    const completedLabel = scopeLabel || active?.targetLabel || currentSiteScopeLabel;
    completedSiteScopeRef.current = { key: completedKey, label: completedLabel };
    siteLoadIndicatorRef.current = null;
    setSiteLoadIndicator(null);
    if (siteLoadClearTimerRef.current) {
      window.clearTimeout(siteLoadClearTimerRef.current);
      siteLoadClearTimerRef.current = null;
    }
  }, [currentSiteScopeKey, currentSiteScopeLabel]);

  const scheduleSiteLoadClear = useCallback((delay = 650) => {
    if (siteLoadClearTimerRef.current) {
      window.clearTimeout(siteLoadClearTimerRef.current);
    }
    siteLoadClearTimerRef.current = window.setTimeout(() => {
      const active = siteLoadIndicatorRef.current;
      if (!active || active.activeRequests > 0) return;
      clearSiteLoadIndicator(active.targetKey, active.targetLabel);
    }, delay);
  }, [clearSiteLoadIndicator]);

  useEffect(() => {
    if (!user?.email || user.userType === 'Commercial' || !currentSiteScopeKey) {
      clearSiteLoadIndicator();
      return undefined;
    }

    const completed = completedSiteScopeRef.current;
    if (completed.key === currentSiteScopeKey && !siteLoadIndicatorRef.current) {
      return undefined;
    }

    if (siteLoadClearTimerRef.current) {
      window.clearTimeout(siteLoadClearTimerRef.current);
      siteLoadClearTimerRef.current = null;
    }

    const next = {
      targetKey: currentSiteScopeKey,
      targetLabel: currentSiteScopeLabel,
      previousLabel: completed.key && completed.key !== currentSiteScopeKey ? completed.label : '',
      activeRequests: 0,
      sawRequest: false,
      startedAt: Date.now(),
    };
    siteLoadIndicatorRef.current = next;
    setSiteLoadIndicator(next);

    siteLoadClearTimerRef.current = window.setTimeout(() => {
      const active = siteLoadIndicatorRef.current;
      if (active?.targetKey === currentSiteScopeKey && !active.sawRequest && active.activeRequests === 0) {
        clearSiteLoadIndicator(active.targetKey, active.targetLabel);
      }
    }, 1400);

    return undefined;
  }, [clearSiteLoadIndicator, currentSiteScopeKey, currentSiteScopeLabel, user?.email, user?.userType]);

  const buildDefaultTransferDraft = currentDraft => {
    const countries = Object.keys(baseLocationTree || {});
    const nextCountry = currentDraft?.country && baseLocationTree[currentDraft.country]
      ? currentDraft.country
      : countries[0] || '';
    const states = Object.keys(baseLocationTree[nextCountry] || {});
    const nextState = currentDraft?.state && baseLocationTree[nextCountry]?.[currentDraft.state]
      ? currentDraft.state
      : states[0] || '';
    const bases = baseLocationTree[nextCountry]?.[nextState] || [];
    const currentBaseAllowed = currentDraft?.base && bases.includes(currentDraft.base);
    const nextBase = currentBaseAllowed
      ? currentDraft.base
      : (bases.find(item => item !== user?.location) || bases[0] || '');
    return {
      country: nextCountry,
      state: nextState,
      base: nextBase,
      role: currentDraft?.role || user?.role || '',
      shop: currentDraft?.shop || user?.shop || '',
      reason: currentDraft?.reason || '',
    };
  };

  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        const res = await api.get('/auth/config');
        setAuthConfig({
          authMode: res.data?.authMode || 'local',
          passwordLoginEnabled: res.data?.passwordLoginEnabled !== false,
          cacLoginEnabled: Boolean(res.data?.cacLoginEnabled),
          appEdition: res.data?.appEdition || APP_EDITION,
          commercialRegistrationEnabled: BUILD_ENABLE_COMMERCIAL_REGISTRATION && res.data?.commercialRegistrationEnabled === true,
          militaryRegistrationEnabled: BUILD_ENABLE_MILITARY_REGISTRATION && res.data?.militaryRegistrationEnabled !== false,
          enabledMilitaryRegistrationTypes: Array.isArray(res.data?.enabledMilitaryRegistrationTypes)
            ? res.data.enabledMilitaryRegistrationTypes
            : ['USAF'],
        });
      } catch (err) {
        console.error('Failed to load auth configuration:', err);
      }
    };

    loadAuthConfig();
  }, []);

  useEffect(() => {
    const preferenceTheme = normalizeUserPreferences(user?.preferences).theme;
    const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    const effectiveTheme = preferenceTheme === 'system'
      ? (systemDark ? 'dark' : 'light')
      : preferenceTheme;
    document.documentElement.dataset.mfeTheme = effectiveTheme;
    document.documentElement.dataset.mfeThemePreference = preferenceTheme;
  }, [user?.preferences]);

  useEffect(() => {
    window.localStorage.setItem('mfeDismissedAnnouncements', JSON.stringify(dismissedAnnouncementIds));
  }, [dismissedAnnouncementIds]);

  useEffect(() => {
    const preferences = normalizeUserPreferences(user?.preferences);
    setPersonnelFieldColumns(mergePersonnelFieldColumns(preferences.tableSettings.personnel.columns));
    setPersonnelCollapseSettings(mergePersonnelCollapseSettings(preferences.tableSettings.personnel.collapse));
  }, [user?.email, user?.preferences]);

  useEffect(() => {
    if (!user?.email) return;
    const preferences = normalizeUserPreferences(user.preferences);
    setShowOnboarding(!preferences.onboardingCompletedAt && !preferences.onboardingSkippedAt);
    setOnboardingStep(0);
  }, [user?.email]);

  useEffect(() => {
    if (isAfRcHeadquartersLocation(user?.location)) {
      setActiveTab('crystalBall');
    }
  }, [user?.email, user?.location, user?.role]);

  useEffect(() => {
    if (!user?.email) return undefined;
    const loadPlatformAnnouncements = async () => {
      try {
        const res = await api.get('/platform-announcements', {
          params: {
            email: user.email,
            location: selectedBase || user.location,
          },
        });
        setPlatformAnnouncements(res.data?.data || []);
      } catch (err) {
        console.error('Failed to load platform announcements:', err);
      }
    };
    loadPlatformAnnouncements();
    const interval = window.setInterval(loadPlatformAnnouncements, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [user?.email, selectedBase]);

  useEffect(() => {
    if (!user?.email || typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const billingStatus = params.get('billing');
    if (!['success', 'cancelled', 'portal_return'].includes(billingStatus)) return;

    setBillingReturn({
      status: billingStatus,
      planKey: params.get('plan') || '',
      sessionId: params.get('session_id') || '',
    });
    setActiveTab('upgrade');

    params.delete('billing');
    params.delete('session_id');
    params.delete('plan');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, [user?.email]);

  useEffect(() => {
    const supportLocations = user?.accessible_support_locations || [];
    const supportLocationKey = supportLocations.join('|');
    const location = selectedBase || user?.location || supportLocations[0] || '';

    if (
      !user?.email ||
      user?.userType === 'Commercial' ||
      user?.can_access_operational_data === false ||
      (user?.is_support_user && !supportLocationKey) ||
      !location ||
      !weatherEnabled
    ) {
      setWeatherSummary(null);
      return undefined;
    }

    let isStale = false;
    const loadWeatherSummary = async () => {
      try {
        const res = await api.get('/weather/summary', {
          params: {
            email: user.email,
            location,
          },
        });
        if (!isStale) setWeatherSummary(res.data || null);
      } catch (err) {
        console.error('Failed to load weather summary:', err);
        if (!isStale && err.response?.status === 403) {
          setWeatherSummary(null);
        }
      }
    };

    loadWeatherSummary();
    const interval = window.setInterval(loadWeatherSummary, 60 * 60 * 1000);
    return () => {
      isStale = true;
      window.clearInterval(interval);
    };
  }, [
    user?.email,
    user?.userType,
    user?.location,
    user?.can_access_operational_data,
    user?.is_support_user,
    selectedBase,
    user?.accessible_support_locations,
    weatherEnabled,
  ]);

  useEffect(() => {
    const supportLocations = user?.accessible_support_locations || [];
    const supportLocationKey = supportLocations.join('|');
    const location = selectedBase || user?.location || supportLocations[0] || '';

    if (
      !user?.email ||
      user?.userType === 'Commercial' ||
      user?.can_access_operational_data === false ||
      (user?.is_support_user && !supportLocationKey) ||
      !location
    ) {
      setHeaderSubscription(null);
      setIsHeaderSubscriptionLoading(false);
      return undefined;
    }

    let isStale = false;
    const loadHeaderSubscription = async () => {
      try {
        if (!isStale) {
          setIsHeaderSubscriptionLoading(true);
        }
        const res = await api.get('/billing/plans', {
          params: {
            email: user.email,
            location,
          },
        });
        if (!isStale) {
          setHeaderSubscription(res.data?.subscription || null);
        }
      } catch (err) {
        if (!isStale) {
          setHeaderSubscription(null);
        }
      } finally {
        if (!isStale) {
          setIsHeaderSubscriptionLoading(false);
        }
      }
    };

    loadHeaderSubscription();
    return () => {
      isStale = true;
    };
  }, [
    user?.email,
    user?.userType,
    user?.location,
    user?.can_access_operational_data,
    user?.is_support_user,
    selectedBase,
    user?.accessible_support_locations,
  ]);

  useEffect(() => {
    if (!showPersonnelFieldSettings) return undefined;

    const handleClickAway = event => {
      if (personnelFieldSettingsRef.current && !personnelFieldSettingsRef.current.contains(event.target)) {
        setShowPersonnelFieldSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [showPersonnelFieldSettings]);

  // for Issues Log data
  const [issuesData, setIssuesData] = useState([]);

  // for personnel upload handling or button click load file
  const defaultPersonnelRef = useRef(null);
  const personnelFieldSettingsRef = useRef(null);
  const personnelTopScrollRef = useRef(null);
  const personnelTableScrollRef = useRef(null);
  const [personnelScrollMetrics, setPersonnelScrollMetrics] = useState({
    contentWidth: 0,
    hasOverflow: false,
  });
    
  // Tracking which ATO row is selected, used for issueslog transfer
  const [selectedAtoRow, setSelectedAtoRow] = useState(null);
  const handleSelectAtoRow = row => setSelectedAtoRow(row);
  const [selectedAtoIndex, setSelectedAtoIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);  
    
  // for bell‐icon alerts
  const [alerts, setAlerts] = useState([]);
  // track which table cells were edited (rowId|colKey)
  const [editedCells,   setEditedCells]   = useState(new Set());

  // clear‐all bell icon alerts handler
  const clearAlerts = () => {
    setAlerts([]);
  };
  
    
  const personnelColumns = PERSONNEL_TEXT_COLUMNS;
  const booleanFields = PERSONNEL_BOOLEAN_FIELDS;
  const visiblePersonnelColumnKeys = new Set(
    personnelFieldColumns
      .filter(column => column.visible)
      .map(column => column.key)
  );
  const personnelCollapseHiddenKeys = new Set(personnelCollapseSettings.hiddenKeys || []);
  const displayedPersonnelColumnKeys = new Set(
    Array.from(visiblePersonnelColumnKeys).filter(key => (
      !personnelCollapseSettings.collapsed || !personnelCollapseHiddenKeys.has(key)
    ))
  );
  const visiblePersonnelColumns = personnelColumns.filter(key => displayedPersonnelColumnKeys.has(key));
  const visiblePersonnelBooleanFields = booleanFields.filter(key => displayedPersonnelColumnKeys.has(key));
  const isPersonnelLastUploadedVisible = displayedPersonnelColumnKeys.has("lastUploadedAt");
  const personnelSmsEnabled = Boolean(featureFlags.personnel_sms_alerts);
  const personnelAvailabilityPredictionEnabled = Boolean(featureFlags.personnel_availability_prediction);

  useEffect(() => {
    if (activeTab !== 'personnel') return undefined;
    const tableScroll = personnelTableScrollRef.current;
    const topScroll = personnelTopScrollRef.current;
    if (!tableScroll || !topScroll) return undefined;

    const updateScrollMetrics = () => {
      const contentWidth = Math.ceil(tableScroll.scrollWidth);
      const hasOverflow = contentWidth > Math.ceil(tableScroll.clientWidth) + 1;
      setPersonnelScrollMetrics(current => (
        current.contentWidth === contentWidth && current.hasOverflow === hasOverflow
          ? current
          : { contentWidth, hasOverflow }
      ));
      if (topScroll.scrollLeft !== tableScroll.scrollLeft) {
        topScroll.scrollLeft = tableScroll.scrollLeft;
      }
    };

    updateScrollMetrics();
    window.addEventListener('resize', updateScrollMetrics);
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollMetrics);
      resizeObserver.observe(tableScroll);
      const table = tableScroll.querySelector('table');
      if (table) resizeObserver.observe(table);
    }

    return () => {
      window.removeEventListener('resize', updateScrollMetrics);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [
    activeTab,
    personnelCollapseSettings.collapsed,
    visiblePersonnelColumns.length,
    visiblePersonnelBooleanFields.length,
    isPersonnelLastUploadedVisible,
    personnelData.length,
  ]);

  const handlePersonnelTopScroll = event => {
    const tableScroll = personnelTableScrollRef.current;
    if (tableScroll && tableScroll.scrollLeft !== event.currentTarget.scrollLeft) {
      tableScroll.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const handlePersonnelTableScroll = event => {
    const topScroll = personnelTopScrollRef.current;
    if (topScroll && topScroll.scrollLeft !== event.currentTarget.scrollLeft) {
      topScroll.scrollLeft = event.currentTarget.scrollLeft;
    }
  };
  const crystalBallEnabled = Boolean(featureFlags.crystal_ball);
  const normalizedCrystalBallRole = String(user?.role || '').replace(/\s+/g, '').toLowerCase();
  const canUseCrystalBallHq = (
    Boolean(user?.is_superuser)
    ||
    ['commandanalyst', 'hqanalyst', 'majcomanalyst', 'afrchq'].includes(normalizedCrystalBallRole)
    || (isAfRcHeadquartersLocation(user?.location) && normalizedCrystalBallRole === 'siteadmin')
  );
  const canUseCrystalBall = crystalBallEnabled || canUseCrystalBallHq;

  const reorderPersonnelRows = (rows, preferredOrder = []) => {
    const order = new Map(preferredOrder.map((id, index) => [id, index]));
    return [...rows].sort((left, right) => {
      const leftIndex = order.has(left.id) ? order.get(left.id) : Number.MAX_SAFE_INTEGER;
      const rightIndex = order.has(right.id) ? order.get(right.id) : Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });
  };

  const mergePersonnelRowsPreservingOrder = (currentRows, serverRows) => {
    if (!Array.isArray(serverRows)) return currentRows;
    const serverById = new Map(serverRows.map(row => [row.id, row]));
    const merged = currentRows
      .filter(row => serverById.has(row.id))
      .map(row => serverById.get(row.id));
    const currentIds = new Set(currentRows.map(row => row.id));
    const appended = serverRows.filter(row => !currentIds.has(row.id));
    return [...merged, ...appended];
  };

  const getPersonnelBooleanValue = value => (value === 'Yes' ? 'Yes' : 'No');
  const getPersonnelColumnLabel = key => PERSONNEL_FIELD_LABELS[key] || key;
  const getPersonnelColumnClass = key => `personnel-col-${String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

  const parsePersonnelHoursRange = value => {
    const match = String(value || '').trim().match(/^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/);
    if (!match) return null;
    return {
      start: Number(match[1]) * 60 + Number(match[2]),
      end: Number(match[3]) * 60 + Number(match[4]),
      label: `${match[1]}:${match[2]}-${match[3]}:${match[4]}`,
    };
  };

  const parsePersonnelDaysOff = value => {
    const aliasMap = new Map(
      PERSONNEL_DAYS_OFF_OPTIONS.flatMap(option => (
        [option.value, ...(option.aliases || [])].map(alias => [String(alias).toLowerCase(), option.value])
      ))
    );
    return String(value || '')
      .split(/[,&/]+|\s+/)
      .map(part => aliasMap.get(part.trim().toLowerCase()))
      .filter(Boolean)
      .filter((part, index, list) => list.indexOf(part) === index);
  };

  const buildPersonnelAvailabilityPrediction = row => {
    const now = new Date();
    const todayLabel = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const hours = parsePersonnelHoursRange(row.Hours);
    const daysOff = parsePersonnelDaysOff(row["Day Off"]);
    const isDayOff = daysOff.includes(todayLabel);
    const manualPresent = getPersonnelBooleanValue(row["Present?"]) === 'Yes';
    const lastLogin = row._accountLastLoginAt ? new Date(row._accountLastLoginAt) : null;
    const lastLoginIsValid = Boolean(lastLogin && !Number.isNaN(lastLogin.getTime()));
    const minutesSinceLogin = lastLoginIsValid ? Math.round((now.getTime() - lastLogin.getTime()) / 60000) : null;
    const recentLogin = minutesSinceLogin != null && minutesSinceLogin >= 0 && minutesSinceLogin <= 12 * 60;
    const loginLabel = lastLoginIsValid
      ? `${lastLogin.toLocaleDateString()} ${lastLogin.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'No linked login found';

    if (!hours) {
      return {
        predicted: 'Unknown',
        tone: 'unknown',
        manualPresent,
        localTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        todayLabel,
        hoursLabel: row.Hours || 'Not entered',
        daysOffLabel: daysOff.join(', ') || 'None listed',
        loginLabel,
        recentLogin,
        reasons: ['No valid shift-hours range is entered. Use HH:MM-HH:MM.'],
      };
    }

    const isOvernight = hours.end < hours.start;
    const withinHours = isOvernight
      ? nowMinutes >= hours.start || nowMinutes <= hours.end
      : nowMinutes >= hours.start && nowMinutes <= hours.end;
    const predictedPresent = withinHours && !isDayOff;
    const reasons = [
      withinHours ? 'Current local time is inside scheduled hours.' : 'Current local time is outside scheduled hours.',
      isDayOff ? `Today (${todayLabel}) is listed as a day off.` : `Today (${todayLabel}) is not listed as a day off.`,
      recentLogin
        ? 'User has logged into the app within the last 12 hours.'
        : 'No recent linked app login was found within the last 12 hours.',
    ];

    if (manualPresent && !predictedPresent) {
      reasons.push('Manual Present? is Yes, so the member should be treated as actually present.');
    } else if (!manualPresent && predictedPresent) {
      reasons.push('Schedule predicts present, but Present? has not been manually confirmed.');
    } else if (recentLogin && !predictedPresent) {
      reasons.push('Recent login is a supporting signal only; hours/days-off still predict not present.');
    }

    return {
      predicted: predictedPresent ? 'Yes' : 'No',
      tone: predictedPresent ? 'yes' : 'no',
      manualPresent,
      localTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      todayLabel,
      hoursLabel: hours.label,
      daysOffLabel: daysOff.join(', ') || 'None listed',
      loginLabel,
      recentLogin,
      reasons,
    };
  };

  const closePersonnelAvailabilityPopover = useCallback(() => {
    setActivePersonnelAvailabilityId(null);
    setPersonnelAvailabilityAnchorRect(null);
  }, []);

  const togglePersonnelAvailabilityPopover = (rowId, event) => {
    event?.stopPropagation();
    setActivePersonnelAvailabilityId(current => {
      if (current === rowId) {
        setPersonnelAvailabilityAnchorRect(null);
        return null;
      }
      const rect = event?.currentTarget?.getBoundingClientRect();
      setPersonnelAvailabilityAnchorRect(rect ? {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      } : null);
      return rowId;
    });
  };

  useEffect(() => {
    if (activePersonnelAvailabilityId == null) return undefined;
    const closeOnViewportChange = () => closePersonnelAvailabilityPopover();
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    window.visualViewport?.addEventListener('resize', closeOnViewportChange);
    return () => {
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
      window.visualViewport?.removeEventListener('resize', closeOnViewportChange);
    };
  }, [activePersonnelAvailabilityId, closePersonnelAvailabilityPopover]);

  const getPersonnelAvailabilityPopoverStyle = () => {
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 1024;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 768;
    const margin = 12;
    const gap = 8;
    const width = Math.min(320, Math.max(220, viewportWidth - margin * 2));

    if (!personnelAvailabilityAnchorRect) {
      return {
        top: `${margin}px`,
        left: `${Math.max(margin, viewportWidth - width - margin)}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(140, viewportHeight - margin * 2)}px`,
      };
    }

    const targetCenter = personnelAvailabilityAnchorRect.left + personnelAvailabilityAnchorRect.width / 2;
    const left = Math.max(margin, Math.min(targetCenter - width / 2, viewportWidth - width - margin));
    const spaceBelow = viewportHeight - personnelAvailabilityAnchorRect.bottom - gap - margin;
    const spaceAbove = personnelAvailabilityAnchorRect.top - gap - margin;
    const placeBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
    const availableSpace = Math.max(140, placeBelow ? spaceBelow : spaceAbove);
    const maxHeight = Math.min(360, availableSpace);
    const top = placeBelow
      ? Math.min(personnelAvailabilityAnchorRect.bottom + gap, viewportHeight - margin - maxHeight)
      : Math.max(margin, personnelAvailabilityAnchorRect.top - gap - maxHeight);

    return {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(width)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
    };
  };
  
  // Auto-save a single personnel field by UUID
  const handlePersonnelChange = async (id, field, value) => {
    const isBooleanToggle = booleanFields.includes(field);
    const currentVisibleOrder = displayedPersonnelData.map(row => row.id);

    if (isBooleanToggle && personnelSortConfig?.key === field) {
      setPersonnelSortConfig({ key: "Last Name", direction: "asc" });
    }

    setPersonnelData(prev => {
      const nextRows = prev.map(r => (r.id === id ? { ...r, [field]: value } : r));
      return isBooleanToggle
        ? reorderPersonnelRows(nextRows, currentVisibleOrder)
        : nextRows;
    });
  
    try {
      const res = await api.post('/update-personnel-entry', {
        email: user.email,
        location: selectedBase,
        id,
        field,
        value
      });
  
      if (Array.isArray(res.data?.personnel)) {
        setPersonnelData(prev => mergePersonnelRowsPreservingOrder(prev, res.data.personnel));
      }
  
    } catch (err) {
      console.error("❌ Error updating personnel:", err);
      setNotification(err.response?.data?.message || "Failed to update personnel");
      setTimeout(() => setNotification(""), 3000);
      await fetchData("personnel");
    }
  };


  // Add an empty Personnel row
  const handleAddPersonnelRow = async () => {
    const emptyPerson = {
      "Last Name": '',
      "First Name": '',
      "Rank": '',
      "Emp Num": '',
      "Email": '',
      "Mobile Phone": '',
      "SMS Opt-In": 'No',
      "Role": '',
      "Base Code": '',
      "Squadron": '',
      "Shop Name": '',
      "Shop Code": '',
      "Shift": '',
      "Hours": '',
      "Day Off": '',
      "Skill Level": '',
      "Labor Category": '',
      "Xs": 'No',
      "Shop Lead?": 'No',
      "Launch?": 'No',
      "Recovery?": 'No',
      "Present?": 'No',
      "Eligible for Cross-Location Support?": 'No',
      lastUploadedAt: new Date().toISOString()
    };
    // 2) Persist to server
    try {
      const res = await api.post("/add-personnel-entry", {
        email: user.email,
        location: selectedBase,
        record: emptyPerson
      });
      // 3) Re-fetch to get any server‑side defaults if you want
      if (Array.isArray(res.data?.personnel)) {
        setPersonnelData(res.data.personnel);
      } else if (res.data?.entry) {
        setPersonnelData(prev => [...prev, res.data.entry]);
      } else {
        await fetchData("personnel");
      }
    } catch (err) {
      console.error("Failed to save new Personnel row", err);
      setNotification(err.response?.data?.message || "Failed to add personnel row");
      setTimeout(() => setNotification(""), 3000);
    }
  };
  
  const handlePersonnelFieldColumnsChange = nextColumns => {
    setPersonnelFieldColumns(nextColumns);
    const nextCollapseSettings = {
      ...personnelCollapseSettings,
      hiddenKeys: personnelCollapseSettings.hiddenKeys.filter(key => nextColumns.some(column => column.key === key && column.visible)),
    };
    setPersonnelCollapseSettings(nextCollapseSettings);
    const visibleKeys = new Set(nextColumns.filter(column => column.visible).map(column => column.key));
    if (personnelSortConfig?.key && !visibleKeys.has(personnelSortConfig.key)) {
      setPersonnelSortConfig({ key: "Last Name", direction: "asc" });
    }
    if (user?.email) {
      const currentPreferences = normalizeUserPreferences(user.preferences);
      saveUserPreferencesPatch({
        ...currentPreferences,
        tableSettings: {
          ...currentPreferences.tableSettings,
          personnel: {
            ...currentPreferences.tableSettings.personnel,
            columns: nextColumns,
            collapse: nextCollapseSettings,
          },
        },
      }).catch(err => {
        console.error('Failed to save Personnel field settings', err);
      });
    }
  };

  const handlePersonnelCollapseSettingsChange = updater => {
    setPersonnelCollapseSettings(prev => {
      const nextCollapseSettings = typeof updater === 'function' ? updater(prev) : updater;
      if (user?.email) {
        const currentPreferences = normalizeUserPreferences(user.preferences);
        saveUserPreferencesPatch({
          ...currentPreferences,
          tableSettings: {
            ...currentPreferences.tableSettings,
            personnel: {
              ...currentPreferences.tableSettings.personnel,
              columns: personnelFieldColumns,
              collapse: nextCollapseSettings,
            },
          },
        }).catch(err => {
          console.error('Failed to save Personnel collapse settings', err);
        });
      }
      return nextCollapseSettings;
    });
  };

  const personnelTableColumns = PERSONNEL_FIELD_COLUMNS;
  const personnelSelectFilterKeys = new Set([
    "Shop Name",
    "Squadron",
    "Shop Code",
    "Role",
    "SMS Opt-In",
    "Shift",
    "Day Off",
    "Labor Category",
    "DAFSC"
  ]);
  const personnelFilterOptions = {
    "Shop Name": getUniqueOptions(personnelData, row => row["Shop Name"]),
    "Squadron": PERSONNEL_SQUADRON_OPTIONS,
    "Shop Code": getUniqueOptions(personnelData, row => row["Shop Code"]),
    "Role": getUniqueOptions(personnelData, row => row["Role"]),
    "SMS Opt-In": getUniqueOptions(personnelData, row => row["SMS Opt-In"]),
    "Shift": getUniqueOptions(personnelData, row => row["Shift"]),
    "Day Off": getUniqueOptions(personnelData, row => row["Day Off"]),
  "Labor Category": PERSONNEL_LABOR_CATEGORY_OPTIONS,
  "DAFSC": getUniqueOptions(personnelData, row => row["DAFSC"]),
  };

  const getPersonnelCellValue = (row, key) => {
    if (key === "lastUploadedAt") {
      return row.lastUploadedAt ? formatLocalTimestamp(row.lastUploadedAt) : "";
    }

    return row[key];
  };

  const handlePersonnelSort = key => {
    setPersonnelSortConfig(current => getNextSortConfig(current, key));
  };

  const handlePersonnelFilterChange = (key, value) => {
    setPersonnelFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearPersonnelFilter = key => {
    setPersonnelFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearAllPersonnelFilters = () => {
    setPersonnelFilters({});
  };

  const personnelFilterChips = Object.entries(personnelFilters).flatMap(([key, value]) => {
    if (!value || value === 'any') return [];

    const displayValue = booleanFields.includes(key)
      ? (value === 'yes' ? 'Yes' : 'No')
      : value;

    return [{
      key,
      label: `${getPersonnelColumnLabel(key)}: ${displayValue}`
    }];
  });

  const matchesPersonnelFilter = (row, key) => {
    if (booleanFields.includes(key)) {
      return matchesTriStateFilter(row[key], personnelFilters[key]);
    }

    if (personnelSelectFilterKeys.has(key)) {
      return matchesSelectFilter(row[key], personnelFilters[key]);
    }

    if (key === "lastUploadedAt") {
      return matchesDateFilter(row.lastUploadedAt, personnelFilters.lastUploadedAt);
    }

    return matchesColumnFilter(getPersonnelCellValue(row, key), personnelFilters[key]);
  };

  const renderPersonnelFilterCell = key => {
    if (booleanFields.includes(key)) {
      return (
        <div className="table-filter-select">
          <select
            value={personnelFilters[key] || "any"}
            onChange={e => handlePersonnelFilterChange(key, e.target.value)}
          >
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      );
    }

    if (personnelSelectFilterKeys.has(key)) {
      return (
        <div className="table-filter-select">
          <select
            value={personnelFilters[key] || ""}
            onChange={e => handlePersonnelFilterChange(key, e.target.value)}
          >
            <option value="">All</option>
            {personnelFilterOptions[key].map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    if (key === "lastUploadedAt") {
      return (
        <ClearableInput
          type="date"
          value={personnelFilters.lastUploadedAt || ""}
          onChange={e => handlePersonnelFilterChange("lastUploadedAt", e.target.value)}
        />
      );
    }

    return (
      <ClearableInput
        type="text"
        value={personnelFilters[key] || ''}
        onChange={e => handlePersonnelFilterChange(key, e.target.value)}
        placeholder={`Filter ${getPersonnelColumnLabel(key)}`}
      />
    );
  };

  const displayedPersonnelData = personnelData
    .filter(row =>
      personnelTableColumns.every(col =>
        matchesPersonnelFilter(row, col.key)
      )
    )
    .slice()
    .sort((left, right) => {
      if (!personnelSortConfig) return 0;

      return compareTableValues(
        getPersonnelCellValue(left, personnelSortConfig.key),
        getPersonnelCellValue(right, personnelSortConfig.key),
        personnelSortConfig.direction
      );
    });
  const isPersonnelTableRefreshing = Boolean(personnelLoadingScope) || Boolean(
    personnelDataScope.key &&
    currentSiteScopeKey &&
    personnelDataScope.key !== currentSiteScopeKey
  );
  const personnelRefreshTargetLabel = personnelLoadingScope?.label || currentSiteScopeLabel || selectedBase || 'selected site';
  const personnelRefreshPreviousLabel = personnelLoadingScope?.previousLabel || (
    personnelDataScope.key && personnelDataScope.key !== currentSiteScopeKey
      ? personnelDataScope.label
      : ''
  );

  // Registration options are deployment-controlled. GovCloud defaults to military only;
  // use true/false, 1/0, yes/no, or on/off in env flags.
  const userTypes = [
    ...(authConfig.commercialRegistrationEnabled ? ['Commercial'] : []),
    ...(authConfig.militaryRegistrationEnabled ? ['USAF', 'USArmy'].filter(type => (
      authConfig.enabledMilitaryRegistrationTypes || ['USAF']
    ).includes(type)) : []),
  ];
  const isAfRcHeadquartersRegistration = isAfRcHeadquartersLocation(base);
  const usafRoleOptions = isAfRcHeadquartersRegistration
    ? [...MILITARY_ROLE_OPTIONS, COMMAND_ANALYST_ROLE]
    : MILITARY_ROLE_OPTIONS;
  const personnelRankOptions = PERSONNEL_RANK_OPTIONS;
  const personnelRoleOptions = MILITARY_ROLE_OPTIONS;
  const usafShopOptions  = MILITARY_SHOP_OPTIONS;  


  const canFetchOperationalData = () => {
    if (!user || !canUserAccessOperationalData(user)) return false;
    if (!user.is_support_user) return true;

    const supportLocations = user.accessible_support_locations || [];
    return Boolean(selectedBase && supportLocations.includes(selectedBase));
  };

  // state & fetch for issues, TODO see if issues component should fetch
  const fetchIssues = async () => {
    if (!canFetchOperationalData()) return;
    try {
      const res = await api.get('/get-issues-log', { params: { email: user.email, location: selectedBase } });
      setIssuesData(res.data.data);
    } catch (err) {
      console.error("Error fetching issues log", err);
    }
  };

  const isApprovedRegistrationEmail = (candidateEmail, candidateUserType) => {
    const normalizedEmail = candidateEmail.trim().toLowerCase();

    if (!normalizedEmail || !candidateUserType) {
      return { ok: false, message: 'Enter your email and select a user type first.' };
    }

    if (candidateUserType === 'USAF') {
      const ok = normalizedEmail.endsWith('@us.af.mil') || normalizedEmail.endsWith('@mail.mil');
      return { ok, message: 'Invalid USAF address.' };
    }

    if (candidateUserType === 'USArmy') {
      const ok =
        normalizedEmail.endsWith('@usa.army.mil') ||
        normalizedEmail.endsWith('@army.mil') ||
        normalizedEmail.endsWith('@mail.mil');
      return { ok, message: 'Invalid USArmy address.' };
    }

    return { ok: true };
  };

  const resetRegistrationVerificationState = ({ clearAttemptKey = true, clearResendFlag = true } = {}) => {
    setIsCodeSent(false);
    setIsCodeVerified(false);
    setRegCode('');
    if (clearAttemptKey) {
      setLastCommercialCodeAttemptKey('');
    }
    if (clearResendFlag) {
      setCommercialCodeNeedsResend(false);
    }
  };

  const updateCommercialRegistration = updater => {
    setCommercialRegistration(current => {
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      if (!next.companyDomain?.trim()) {
        next.restrictSignupToCompanyDomain = false;
      }
      if (next.commercialRegistrationMode === 'join') {
        next.companyDomain = '';
        next.restrictSignupToCompanyDomain = false;
      }

      const currentAttemptKey = getCommercialRegistrationAttemptKey(email, current);
      const nextAttemptKey = getCommercialRegistrationAttemptKey(email, next);
      if (
        currentAttemptKey !== nextAttemptKey &&
        (isCodeSent || isCodeVerified || Boolean(lastCommercialCodeAttemptKey))
      ) {
        resetRegistrationVerificationState({ clearAttemptKey: false, clearResendFlag: false });
        setCommercialCodeNeedsResend(true);
      }

      return next;
    });
  };

  const requestRegistrationCode = async (
    candidateEmail = email,
    candidateUserType = userType,
    { forceResend = false } = {}
  ) => {
    if (!userTypes.includes(candidateUserType)) {
      return false;
    }

    const normalizedEmail = candidateEmail.trim().toLowerCase();
    const validation = isApprovedRegistrationEmail(normalizedEmail, candidateUserType);

    if (!validation.ok) {
      if (candidateUserType) {
        alert(validation.message);
      }
      return false;
    }

    if (candidateUserType === 'Commercial' && !isCommercialRegistrationReady(commercialRegistration)) {
      return false;
    }

    if (!forceResend && (isCodeSent || isCodeVerified)) {
      return false;
    }

    if (isSendingRegistrationCode) {
      return false;
    }

    try {
      setIsSendingRegistrationCode(true);
      await api.post('/request-registration-code', {
        email: normalizedEmail,
        userType: candidateUserType,
        ...(candidateUserType === 'Commercial' ? buildCommercialRegistrationPayload(commercialRegistration) : {}),
      });
      if (candidateUserType === 'Commercial') {
        setLastCommercialCodeAttemptKey(getCommercialRegistrationAttemptKey(normalizedEmail, commercialRegistration));
        setCommercialCodeNeedsResend(false);
      }
      setIsCodeSent(true);
      setIsCodeVerified(false);
      setRegCode('');
      alert('Verification code sent.');
      return true;
    } catch (err) {
      if (candidateUserType === 'Commercial') {
        setLastCommercialCodeAttemptKey(getCommercialRegistrationAttemptKey(normalizedEmail, commercialRegistration));
      }
      alert('Error sending verification code: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setIsSendingRegistrationCode(false);
    }
  };

  const loadRegistrationLocationAdminStatus = async (location = base, candidateUserType = userType) => {
    if (!location || !['USAF', 'USArmy'].includes(candidateUserType || '')) {
      setRegistrationLocationAdminStatus(null);
      return null;
    }
    try {
      setIsLoadingRegistrationAdminStatus(true);
      const res = await api.get('/registration/location-admin-status', {
        params: {
          location,
          userType: candidateUserType,
        },
      });
      setRegistrationLocationAdminStatus(res.data || null);
      return res.data || null;
    } catch (err) {
      setRegistrationLocationAdminStatus({
        message: err.response?.data?.message || 'Unable to check siteAdmin status for this location.',
      });
      return null;
    } finally {
      setIsLoadingRegistrationAdminStatus(false);
    }
  };

  useEffect(() => {
    if (
      isLogin ||
      !isCodeVerified ||
      !['USAF', 'USArmy'].includes(userType || '') ||
      !base
    ) {
      setRegistrationLocationAdminStatus(null);
      return;
    }
    loadRegistrationLocationAdminStatus(base, userType);
  }, [base, isCodeVerified, isLogin, userType]);

  useEffect(() => {
    if (isLogin || userType !== 'Commercial') return;
    if (!email.trim() || isCodeSent || isCodeVerified || isSendingRegistrationCode) return;
    if (!isCommercialRegistrationReady(commercialRegistration)) return;
    if (commercialCodeNeedsResend) return;
    if (lastCommercialCodeAttemptKey) return;

    requestRegistrationCode(email, 'Commercial');
  }, [
    commercialCodeNeedsResend,
    commercialRegistration,
    email,
    isCodeSent,
    isCodeVerified,
    isLogin,
    isSendingRegistrationCode,
    lastCommercialCodeAttemptKey,
    userType
  ]);

  const applyAuthenticatedUser = useCallback((data) => {
    const {
      email: userEmail,
      location,
      role: userRole,
      shop: userShop,
      is_superuser,
      is_support_user,
      accessible_support_locations,
      can_access_operational_data,
      superuserOperationalAccessMode,
      userType: responseUserType,
      commercialTenantId,
      cacLinked,
      cacIdentityLabel,
      cacLinkedAt,
      preferences
    } = data;
    const normalizedPreferences = normalizeUserPreferences(preferences);

    setUser({
      email: userEmail,
      location,
      role: userRole,
      shop: userShop,
      is_superuser,
      is_support_user,
      accessible_support_locations: accessible_support_locations || [],
      can_access_operational_data,
      superuserOperationalAccessMode,
      userType: responseUserType,
      commercialTenantId,
      cacLinked: Boolean(cacLinked),
      cacIdentityLabel: cacIdentityLabel || '',
      cacLinkedAt: cacLinkedAt || '',
      preferences: normalizedPreferences
    });
    setProfileDraft(normalizedPreferences);
    setPassword('');
    setConfirmPassword('');
    setNewPassword('');
    setResetCode('');
    setAuthSecretClearNonce(nonce => nonce + 1);
    const firstSupportLocation = (accessible_support_locations || [])[0] || '';
    setSelectedBase(is_support_user ? firstSupportLocation : (location || (is_superuser ? DEMO_LOCATION_NAME : '')));
    setLocationSharingNotice(null);
    setLocationSharingNoticeDismissed(false);
    setLocationSharingRetryNonce(0);
    if (is_superuser) {
      setActiveTab('ownerPortal');
    }
  }, []);

  useEffect(() => {
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'];
    events.forEach(eventName => {
      window.addEventListener(eventName, markUserActivityForSession, { passive: true });
    });
    return () => {
      events.forEach(eventName => {
        window.removeEventListener(eventName, markUserActivityForSession);
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const res = await api.get('/me');
        if (!cancelled && res.data?.email) {
          applyAuthenticatedUser(res.data);
        }
      } catch (err) {
        if (err.response?.data?.sessionExpired) {
          console.info('Saved session expired due to inactivity.');
        }
      } finally {
        if (!cancelled) {
          setIsSessionRestorePending(false);
        }
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [applyAuthenticatedUser]);

  const completeRegistration = async () => {
    if (!termsAccepted) {
      alert('Please accept the MFE Terms and Conditions before registering.');
      return null;
    }
    if (!userTypes.includes(userType)) {
      alert('This registration type is not enabled for this deployment.');
      return null;
    }
    if (userType === 'USAF' || userType === 'USArmy') {
      if (!base) {
        alert('Please select your location before registering.');
        return null;
      }
      if (!role) {
        alert('Please select your role before registering.');
        return null;
      }
      if (!shop) {
        alert('Please select your shop before registering.');
        return null;
      }
    }

    const normalizedEmail = email.trim().toLowerCase();
    const passwordError = getPasswordStrengthError(password, normalizedEmail);
    if (passwordError) {
      alert(passwordError);
      return null;
    }

    const payload = {
      email: normalizedEmail,
      password,
      confirmPassword,
      userType,
      location: userType === 'USAF' || userType === 'USArmy' ? base : '',
      role: userType === 'Commercial' ? '' : role,
      shop: userType === 'Commercial' ? '' : shop,
      termsAccepted,
      termsVersion: CURRENT_TERMS_VERSION,
      ...(userType === 'Commercial' ? buildCommercialRegistrationPayload(commercialRegistration) : {})
    };

    const res = await api.post('/register', payload);
    if (res.data.message === "Registration successful") {
      const roleNotice = res.data.roleNotice ? `\n\n${res.data.roleNotice}` : '';
      alert(`Registration successful. You are now logged in.${roleNotice}`);
    }
    if (res.data.email) {
      applyAuthenticatedUser(res.data);
    }
    return res;
  };

  const handleCacLogin = async () => {
    if (isAuthSubmitting) return;

    setIsAuthSubmitting(true);

    let cacOrigin;
    try {
      cacOrigin = new URL(CAC_API_BASE_URL).origin;
    } catch (err) {
      console.error(err);
      alert('CAC / SSO login is not configured correctly.');
      setIsAuthSubmitting(false);
      return;
    }

    const popupUrl = `${CAC_API_BASE_URL.replace(/\/$/, '')}/cac-login`;
    const popup = window.open(
      popupUrl,
      'mfe-cac-login',
      'popup=yes,width=520,height=620'
    );

    if (!popup) {
      alert('Your browser blocked the CAC / SSO login popup. Allow popups for MFE and try again.');
      setIsAuthSubmitting(false);
      return;
    }

    let completed = false;
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(closeCheck);
    };
    const handleMessage = event => {
      if (event.origin !== cacOrigin || event.data?.type !== 'mfe:cac-login') {
        return;
      }
      completed = true;
      cleanup();
      setIsAuthSubmitting(false);
      if (event.data.ok && event.data.payload) {
        applyAuthenticatedUser(event.data.payload);
      } else {
        alert(`CAC / PIV login failed. ${CAC_WRONG_CERTIFICATE_HINT}`);
      }
    };
    const closeCheck = setInterval(() => {
      if (popup.closed && !completed) {
        cleanup();
        setIsAuthSubmitting(false);
      }
    }, 500);

    window.addEventListener('message', handleMessage);
  };

  const handleCacLink = async () => {
    if (!user?.email || isLinkingCac) return;
    if (!cacLinkPassword) {
      setProfileError('Enter your current password before linking CAC / PIV.');
      setProfileMessage('');
      return;
    }

    setIsLinkingCac(true);
    setProfileError('');
    setProfileMessage('');

    let cacOrigin;
    try {
      cacOrigin = new URL(CAC_API_BASE_URL).origin;
    } catch (err) {
      console.error(err);
      setProfileError('CAC / SSO login is not configured correctly.');
      setIsLinkingCac(false);
      return;
    }

    let token;
    try {
      const res = await api.post('/cac-link-token', {
        email: user.email,
        password: cacLinkPassword,
      });
      token = res.data?.token;
    } catch (err) {
      console.error(err);
      setProfileError(err.response?.data?.message || 'Unable to start CAC / PIV linking.');
      setIsLinkingCac(false);
      return;
    }

    if (!token) {
      setProfileError('Unable to start CAC / PIV linking.');
      setIsLinkingCac(false);
      return;
    }

    const popupUrl = `${CAC_API_BASE_URL.replace(/\/$/, '')}/cac-link?token=${encodeURIComponent(token)}`;
    const popup = window.open(
      popupUrl,
      'mfe-cac-link',
      'popup=yes,width=520,height=620'
    );

    if (!popup) {
      setProfileError('Your browser blocked the CAC / PIV linking popup. Allow popups for MFE and try again.');
      setIsLinkingCac(false);
      return;
    }

    let completed = false;
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(closeCheck);
    };
    const handleMessage = event => {
      if (event.origin !== cacOrigin || event.data?.type !== 'mfe:cac-link') {
        return;
      }
      completed = true;
      cleanup();
      setIsLinkingCac(false);
      if (event.data.ok && event.data.payload) {
        applyAuthenticatedUser(event.data.payload);
        setCacLinkPassword('');
        setProfileMessage(event.data.message || 'CAC / PIV linked. You can use CAC login next time.');
      } else {
        setProfileError(event.data?.message || 'CAC / PIV linking failed.');
      }
    };
    const closeCheck = setInterval(() => {
      if (popup.closed && !completed) {
        cleanup();
        setIsLinkingCac(false);
      }
    }, 500);

    window.addEventListener('message', handleMessage);
  };


  const handleAuth = async () => {
    if (isAuthSubmitting) return;

    try {
      setIsAuthSubmitting(true);
      if (isLogin) {
          // call your login endpoint
          const res = await api.post('/login', {
            email:    email.trim().toLowerCase(),
            password: password
          });
          applyAuthenticatedUser(res.data);
          return;
      }      
      if (!isLogin) {
        if (!termsAccepted) {
          alert('Please accept the MFE Terms and Conditions before registering.');
          return;
        }
        if (!userTypes.includes(userType)) {
          alert('Select an enabled registration type.');
          return;
        }
        if (password !== confirmPassword) {
          alert('Passwords do not match.');
          return;
        }

        if (userType && !isCodeVerified) {
          alert('Please verify your email first.');
          return;
        }
        await completeRegistration();
      }    
    } catch (err) {
          console.error(err);
          const message = err.response?.data?.message;

          if (!isLogin && message === 'User already exists') {
            alert('That email is already registered. Try logging in, or use Forgot Password if you no longer know the password.');
            return;
          }

          alert(message || "Auth failed");
        } finally {
          setIsAuthSubmitting(false);
        }
  };


  const clearAuthenticatedUserState = useCallback(() => {
    setUser(null);
    setSelectedBase('');
    setGlobalView(false);
    setIsProfileOpen(false);
    setPassword('');
    setConfirmPassword('');
    setNewPassword('');
    setResetCode('');
    setAuthSecretClearNonce(nonce => nonce + 1);
    setCacLinkPassword('');
    setAccountClosePassword('');
    setAccountCloseConfirm('');
    setLocationSharingNotice(null);
    setLocationSharingNoticeDismissed(false);
    setLocationSharingRetryNonce(0);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (err) {
      console.warn('Backend logout did not complete; clearing local session state.', err);
    } finally {
      clearAuthenticatedUserState();
    }
  };

  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && error.response?.data?.sessionExpired) {
          clearAuthenticatedUserState();
          setIsSessionRestorePending(false);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, [clearAuthenticatedUserState]);

  useEffect(() => {
    const requestInterceptorId = api.interceptors.request.use(config => {
      const active = siteLoadIndicatorRef.current;
      const scopedKey = requestScopeKey(config);
      if (active && scopedKey && scopedKey === active.targetKey) {
        if (siteLoadClearTimerRef.current) {
          window.clearTimeout(siteLoadClearTimerRef.current);
          siteLoadClearTimerRef.current = null;
        }
        const next = {
          ...active,
          activeRequests: active.activeRequests + 1,
          sawRequest: true,
        };
        config.mfeSiteLoadScopeKey = scopedKey;
        siteLoadIndicatorRef.current = next;
        setSiteLoadIndicator(next);
      }
      return config;
    });

    const settleScopedRequest = config => {
      const scopedKey = config?.mfeSiteLoadScopeKey;
      const active = siteLoadIndicatorRef.current;
      if (!scopedKey || !active || scopedKey !== active.targetKey) return;
      const next = {
        ...active,
        activeRequests: Math.max(0, active.activeRequests - 1),
      };
      siteLoadIndicatorRef.current = next;
      setSiteLoadIndicator(next);
      if (next.activeRequests === 0 && next.sawRequest) {
        scheduleSiteLoadClear(650);
      }
    };

    const responseInterceptorId = api.interceptors.response.use(
      response => {
        settleScopedRequest(response.config);
        return response;
      },
      error => {
        settleScopedRequest(error.config);
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptorId);
      api.interceptors.response.eject(responseInterceptorId);
    };
  }, [scheduleSiteLoadClear]);

  const closeAccountAccess = async () => {
    if (!user?.email || isClosingAccount) return;
    if (!accountClosePassword) {
      setProfileMessage('');
      setProfileError('Enter your current password before closing account access.');
      return;
    }
    if (accountCloseConfirm.trim().toUpperCase() !== 'CLOSE') {
      setProfileMessage('');
      setProfileError('Type CLOSE to confirm account access deactivation.');
      return;
    }

    const confirmed = window.confirm('Deactivate your MFE account access and sign out? Your historical activity will remain in unit records.');
    if (!confirmed) return;

    try {
      setIsClosingAccount(true);
      setProfileMessage('');
      setProfileError('');
      const res = await api.post('/account-access/deactivate', {
        email: user.email,
        currentPassword: accountClosePassword,
        confirmation: accountCloseConfirm,
        reason: 'Self-service account access closure from Profile.',
      });
      alert(res.data?.message || 'Your account access has been deactivated. You have been signed out.');
      handleLogout();
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Unable to deactivate account access.');
    } finally {
      setIsClosingAccount(false);
    }
  };

  const openProfileSettings = () => {
    setProfileDraft(normalizeUserPreferences(user?.preferences));
    setLocationTransferDraft(prev => buildDefaultTransferDraft(prev));
    setSiteAdminClaimStatus(null);
    setCacLinkPassword('');
    setAccountClosePassword('');
    setAccountCloseConfirm('');
    setProfileMessage('');
    setProfileError('');
    setIsProfileOpen(true);
  };

  const loadLocationTransferRequests = async () => {
    if (!user?.email || !canRequestLocationTransfer) {
      setLocationTransferRequests([]);
      return [];
    }
    const res = await api.get('/location-transfer-requests', {
      params: { email: user.email },
    });
    const rows = res.data?.data || [];
    setLocationTransferRequests(rows);
    return rows;
  };

  const loadSiteAdminClaimStatus = async () => {
    if (!user?.email || !selectedBase || !['USAF', 'USArmy'].includes(user?.userType || '')) {
      setSiteAdminClaimStatus(null);
      return null;
    }
    const res = await api.get('/site-admin-claim/status', {
      params: {
        email: user.email,
        location: selectedBase,
      },
    });
    setSiteAdminClaimStatus(res.data || null);
    return res.data || null;
  };

  const claimSiteAdmin = async () => {
    if (!user?.email || !selectedBase || isClaimingSiteAdmin) return;
    const confirmed = window.confirm(
      `Claim siteAdmin for ${selectedBase}? This gives you access to manage users, roles, and support access for this location.`
    );
    if (!confirmed) return;

    try {
      setIsClaimingSiteAdmin(true);
      setProfileMessage('');
      setProfileError('');
      const res = await api.post('/site-admin-claim', {
        email: user.email,
        location: selectedBase,
      });
      if (res.data?.email) {
        applyAuthenticatedUser(res.data);
      }
      setSiteAdminClaimStatus(res.data?.claimStatus || null);
      setProfileMessage(res.data?.message || 'You are now the siteAdmin for this location.');
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to claim siteAdmin.';
      setProfileError(message);
      if (err.response?.data) {
        setSiteAdminClaimStatus(err.response.data);
      }
    } finally {
      setIsClaimingSiteAdmin(false);
    }
  };

  const updateLocationTransferDraft = (field, value) => {
    setLocationTransferDraft(prev => {
      if (field === 'country') {
        const states = Object.keys(baseLocationTree[value] || {});
        const nextState = states[0] || '';
        const bases = baseLocationTree[value]?.[nextState] || [];
        return {
          ...prev,
          country: value,
          state: nextState,
          base: bases.find(item => item !== user?.location) || bases[0] || '',
        };
      }
      if (field === 'state') {
        const bases = baseLocationTree[prev.country]?.[value] || [];
        return {
          ...prev,
          state: value,
          base: bases.find(item => item !== user?.location) || bases[0] || '',
        };
      }
      return { ...prev, [field]: value };
    });
    setProfileMessage('');
    setProfileError('');
  };

  const submitLocationTransferRequest = async () => {
    if (!user?.email || isSubmittingLocationTransfer) return;
    if (!locationTransferDraft.base || locationTransferDraft.base === user.location) {
      setProfileError('Choose a gaining location different from your current default.');
      return;
    }

    try {
      setIsSubmittingLocationTransfer(true);
      setProfileMessage('');
      setProfileError('');
      const res = await api.post('/location-transfer-requests', {
        email: user.email,
        toLocation: locationTransferDraft.base,
        requestedRole: locationTransferDraft.role,
        requestedShop: locationTransferDraft.shop,
        reason: locationTransferDraft.reason,
      });
      setLocationTransferRequests(prev => [res.data.request, ...prev.filter(row => row.id !== res.data.request?.id)]);
      setLocationTransferDraft(prev => ({ ...prev, reason: '' }));
      setProfileMessage(res.data?.message || 'Location transfer requested.');
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Unable to request location transfer.');
      if (err.response?.data?.request) {
        setLocationTransferRequests(prev => [err.response.data.request, ...prev.filter(row => row.id !== err.response.data.request.id)]);
      }
    } finally {
      setIsSubmittingLocationTransfer(false);
    }
  };

  useEffect(() => {
    if (!isProfileOpen || !canRequestLocationTransfer) return;
    setLocationTransferDraft(prev => buildDefaultTransferDraft(prev));
    loadLocationTransferRequests().catch(err => {
      setProfileError(err.response?.data?.message || 'Unable to load location transfer requests.');
    });
  }, [isProfileOpen, canRequestLocationTransfer, user?.email, user?.location, baseLocationTree]);

  useEffect(() => {
    if (!isProfileOpen || !user?.email || !selectedBase || !['USAF', 'USArmy'].includes(user?.userType || '')) {
      setSiteAdminClaimStatus(null);
      return;
    }
    loadSiteAdminClaimStatus().catch(err => {
      setProfileError(err.response?.data?.message || 'Unable to check siteAdmin recovery status.');
    });
  }, [isProfileOpen, selectedBase, user?.email, user?.userType]);

  const updateProfileDraft = (path, value) => {
    setProfileDraft(prev => {
      const normalized = normalizeUserPreferences(prev);
      if (path === 'theme') {
        return { ...normalized, theme: value };
      }
      if (String(path).startsWith('sms:')) {
        const smsKey = String(path).slice(4);
        return {
          ...normalized,
          smsNotifications: {
            ...normalized.smsNotifications,
            [smsKey]: value,
          },
        };
      }
      return {
        ...normalized,
        emailNotifications: {
          ...normalized.emailNotifications,
          [path]: value,
        },
      };
    });
    setProfileMessage('');
    setProfileError('');
  };

  const saveProfileSettings = async () => {
    if (!user?.email || isSavingProfile) return;
    try {
      setIsSavingProfile(true);
      setProfileMessage('');
      setProfileError('');
      const res = await api.patch('/user-preferences', {
        email: user.email,
        preferences: profileDraft,
      });
      const preferences = normalizeUserPreferences(res.data.preferences);
      setUser(prev => prev ? { ...prev, preferences } : prev);
      setProfileDraft(preferences);
      setProfileMessage('Profile preferences saved.');
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Unable to save profile preferences.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const saveUserPreferencesPatch = async nextPreferences => {
    const normalized = normalizeUserPreferences(nextPreferences);
    const res = await api.patch('/user-preferences', {
      email: user.email,
      preferences: normalized,
    });
    const saved = normalizeUserPreferences(res.data.preferences);
    setUser(prev => prev ? { ...prev, preferences: saved } : prev);
    setProfileDraft(saved);
    return saved;
  };

  const handleAtoTableSettingsChange = useCallback(nextFlyScheduleSettings => {
    if (!user?.email) return;
    const currentPreferences = normalizeUserPreferences(user.preferences);
    saveUserPreferencesPatch({
      ...currentPreferences,
      tableSettings: {
        ...currentPreferences.tableSettings,
        flySchedule: {
          ...currentPreferences.tableSettings.flySchedule,
          ...nextFlyScheduleSettings,
        },
      },
    }).catch(err => {
      console.error('Failed to save Fly Schedule table settings', err);
    });
  }, [user?.email, user?.preferences]);

  const finishOnboarding = async (skipped = false) => {
    if (!user?.email) {
      setShowOnboarding(false);
      return;
    }
    if (!onboardingDontShowAgain) {
      setShowOnboarding(false);
      return;
    }
    const timestamp = new Date().toISOString();
    const current = normalizeUserPreferences(user.preferences);
    const next = {
      ...current,
      onboardingCompletedAt: skipped ? current.onboardingCompletedAt : timestamp,
      onboardingSkippedAt: skipped ? timestamp : current.onboardingSkippedAt,
    };
    try {
      await saveUserPreferencesPatch(next);
    } catch (err) {
      console.error('Unable to save onboarding preference:', err);
    } finally {
      setShowOnboarding(false);
    }
  };

  const dismissPlatformAnnouncement = id => {
    setDismissedAnnouncementIds(prev => Array.from(new Set([...prev, id])));
  };

  const retryLocationSharing = () => {
    setLocationSharingNoticeDismissed(false);
    setLocationSharingNotice(null);
    setLocationSharingRetryNonce(prev => prev + 1);
  };


  const fetchData = async (type) => {
    if (!canFetchOperationalData()) return;
    const params = { email: user.email };
    if (user.is_superuser && globalView) params.global = true;
    else params.location = selectedBase;

    let personnelRequestId = null;
    const requestScopeKey = currentSiteScopeKey;
    const requestScopeLabel = currentSiteScopeLabel;
    if (type === "personnel") {
      personnelRequestId = personnelFetchRequestRef.current + 1;
      personnelFetchRequestRef.current = personnelRequestId;
      setPersonnelLoadingScope({
        key: requestScopeKey,
        label: requestScopeLabel,
        previousLabel: personnelDataScope.key && personnelDataScope.key !== requestScopeKey
          ? personnelDataScope.label
          : '',
      });
    }

    try {
      const res = await api.get(`/get-${type}`, { params });
      if (type === "personnel") {
        if (personnelFetchRequestRef.current !== personnelRequestId) return;
        setPersonnelData(res.data.data);
        setPersonnelDataScope({ key: requestScopeKey, label: requestScopeLabel });
        if (res.data.featureFlags) setFeatureFlags(res.data.featureFlags);
      }
    } finally {
      if (type === "personnel" && personnelFetchRequestRef.current === personnelRequestId) {
        setPersonnelLoadingScope(null);
      }
    }
  };

  const requestResetCode = async () => {
    try {
      await api.post('/request-password-reset', { email });
      alert('Reset code sent to your email.');
      setResetCode(' ');
    } catch (err) {
      alert('Error sending reset code: ' + (err.response?.data?.message || err.message));
    }
  };

  const verifyAndResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }

    const passwordError = getPasswordStrengthError(newPassword, email);
    if (passwordError) {
      alert(passwordError);
      return;
    }

    try {
      await api.post('/verify-password-reset', { email, code: resetCode.trim(), newPassword, confirmPassword });
      alert('Password has been reset.');
      setIsResettingPassword(false);
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      alert('Error resetting password: ' + (err.response?.data?.message || err.message));
    }
  };
  
  {/* TODO: Test a universal data on interval fetcher
  const fetchData = async (type) => {
    const params = { email: user.email };
    if (user.is_superuser && globalView) params.global = true;
    else params.location = selectedBase;
    const res = await api.get(`/get-${type}`, { params });

    // route the response into the correct state setter:
    switch (type) {
      case "ato":
        setAtoData(res.data.data);
        break;
      case "personnel":
        setPersonnelData(res.data.data);
        break;
      case "issues":
        setIssuesData(res.data.data);
        break;
      case "priorities":
        setPriorities(res.data.data);
        break;
      case "changelog":
        setChangeLog(res.data.alerts || res.data.data);
        break;
     default:
        console.warn(`Unknown fetch type: ${type}`);
    }
  };  

  Replace your existing interval‐effect with:
  useEffect(() => {
    if (!user) return;  // nothing to do if not logged in

    // the list of tabs (and endpoints) you want to poll:
    const typesToRefresh = ["ato", "personnel", "issues", "priorities", "changelog"];

    // run them immediately on mount (so you don’t wait 60s for the first load)
    typesToRefresh.forEach(fetchData);

    // then poll every 60s
    const interval = setInterval(() => {
      typesToRefresh.forEach(fetchData);
    }, 60000);

    return () => clearInterval(interval);
  }, [user, selectedBase, globalView]); 
  
  Ensure your tabs read from those state variables
  {activeTab === "ato"       && <ATOScreen    data={atoData}        … />}
  {activeTab === "personnel" && <PersonnelTab data={personnelData}  … />}
  {activeTab === "issues"    && <IssuesTab    data={issuesData}     … />}
  {activeTab === "priorities"&& <PrioritiesTab data={priorities}     … />}
  {activeTab === "changelog" && <ChangeLogTab data={changeLog}      … />}
  
  */}
  

  // Returns location selector component for superusers and normal users alike
  const renderLocationSelector = () => (
    <>
      <label><b>View Location:</b></label>
      {!user.is_superuser && <p>{user.location}</p>}
      {user.is_superuser && (
        <>
          <select value={selectedBase} onChange={e => setSelectedBase(e.target.value)}>
            {Object.entries(baseLocationTree).map(([country, states]) =>
              Object.entries(states).map(([state, bases]) =>
                bases.map(base => (
                  <option key={base} value={base}>{`${country} / ${state} / ${base}`}</option>
                ))
              )
            )}
          </select>
          <label>
            <input type="checkbox" checked={globalView} onChange={e => setGlobalView(e.target.checked)} />
            Global View
          </label>
        </>
      )}
    </>
  );

  // Returns dropdowns for registration location selection
  const renderRegisterDropdowns = () => (
    <>
      <select value={country} onChange={e => { setCountry(e.target.value); setState(''); setBase(''); }}>
        <option value="">Select Country</option>
        {Object.keys(baseLocationTree).map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      {country && (
        <select value={state} onChange={e => { setState(e.target.value); setBase(''); }}>
          <option value="">Select State</option>
          {Object.keys(baseLocationTree[country] || {}).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {state && (
        <select value={base} onChange={e => setBase(e.target.value)}>
          <option value="">Select Base</option>
          {(baseLocationTree[country]?.[state] || []).map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
    </>
  );

  const updateMilpersMappingState = useCallback(payload => {
    setMilpersMappings(prev => ({
      locations: payload?.locations || prev.locations || [],
      locationMappings: payload?.locationMappings || prev.locationMappings || [],
      dafscMappings: payload?.dafscMappings || prev.dafscMappings || [],
      unmappedPersonnelDafscs: payload?.unmappedPersonnelDafscs || prev.unmappedPersonnelDafscs || [],
      shopOptions: payload?.shopOptions || prev.shopOptions || MILITARY_SHOP_OPTIONS,
    }));
    if (Array.isArray(payload?.recentImportJobs)) {
      setMilpersImportJobs(payload.recentImportJobs);
    }
  }, []);

  const showMilpersJobResult = useCallback(job => {
    if (!job) return;
    const result = job.result && typeof job.result === 'object' ? job.result : {};
    setMilpersImportJob(job);
    setMilpersSummary(result.summary || null);
    setMilpersMessage(String(job.status || '').toLowerCase() === 'complete'
      ? result.message || job.message || 'MILPERS import applied.'
      : '');
    setMilpersError(['blocked', 'failed'].includes(String(job.status || '').toLowerCase())
      ? result.message || job.message || 'MILPERS import needs review.'
      : '');
    updateMilpersMappingState(result);
  }, [updateMilpersMappingState]);

  const fetchMilpersMappings = useCallback(async () => {
    if (!user?.email || !user?.is_superuser) return;
    try {
      const res = await api.get('/personnel/milpers/mappings', {
        params: { email: user.email },
      });
      updateMilpersMappingState(res.data);
      const latestJob = Array.isArray(res.data?.recentImportJobs) ? res.data.recentImportJobs[0] : null;
      if (!milpersImportJob && latestJob) {
        showMilpersJobResult(latestJob);
      }
    } catch (err) {
      console.error('Failed to load MILPERS mappings', err);
      setMilpersError(err.response?.data?.message || 'Unable to load MILPERS mappings');
    }
  }, [milpersImportJob, showMilpersJobResult, updateMilpersMappingState, user?.email, user?.is_superuser]);

  useEffect(() => {
    if (activeTab === 'personnel' && user?.is_superuser) {
      fetchMilpersMappings();
    }
  }, [activeTab, user?.is_superuser, fetchMilpersMappings]);

  useEffect(() => () => {
    if (milpersJobPollRef.current) {
      window.clearTimeout(milpersJobPollRef.current);
      milpersJobPollRef.current = null;
    }
  }, []);

  const finishMilpersJob = useCallback(async payload => {
    const job = payload?.job || {};
    const result = job.result && typeof job.result === 'object' ? job.result : payload;
    setMilpersImportJob(job);
    setMilpersMessage(result?.message || job.message || 'MILPERS import applied.');
    setMilpersError('');
    setMilpersSummary(result?.summary || payload?.summary || job.summary || null);
    updateMilpersMappingState(result || payload);
    await fetchData('personnel');
    if (milpersFileRef.current) milpersFileRef.current.value = '';
    setIsMilpersLoading(false);
    setMilpersUploadProgress(null);
    setMilpersProcessingPhase('');
  }, [fetchData, updateMilpersMappingState]);

  const markMilpersJobFailed = useCallback((message, payload = {}) => {
    const job = payload.job || null;
    const result = job?.result && typeof job.result === 'object' ? job.result : payload;
    setMilpersImportJob(job);
    setMilpersError(message || result?.message || 'MILPERS import failed');
    setMilpersSummary(result?.summary || payload.summary || job?.summary || null);
    updateMilpersMappingState(result || payload);
    setIsMilpersLoading(false);
    setMilpersUploadProgress(null);
    setMilpersProcessingPhase('');
  }, [updateMilpersMappingState]);

  const pollMilpersJob = useCallback(async jobId => {
    try {
      const statusRes = await api.get(`/personnel/milpers/import-jobs/${jobId}`, {
        params: { email: user.email },
      });
      const payload = statusRes.data || {};
      const job = payload.job || {};
      const status = String(job.status || '').toLowerCase();
      setMilpersImportJob(job);
      setMilpersUploadProgress(null);
      setMilpersProcessingPhase(job.stage || 'Server is parsing, matching, and applying the roster...');
      if (status === 'complete') {
        await finishMilpersJob(payload);
        return;
      }
      if (status === 'blocked') {
        markMilpersJobFailed(job.message || payload.message || 'MILPERS import needs review before it can apply.', payload);
        return;
      }
      if (status === 'failed') {
        markMilpersJobFailed(job.message || payload.message || 'MILPERS import failed while processing.', payload);
        return;
      }
      milpersJobPollRef.current = window.setTimeout(() => pollMilpersJob(jobId), 2000);
    } catch (err) {
      const payload = err.response?.data || {};
      markMilpersJobFailed(
        payload.message ||
        'Unable to check MILPERS import status. The backend may have restarted while processing this roster.',
        payload
      );
    }
  }, [finishMilpersJob, markMilpersJobFailed, user?.email]);

  const handleMilpersUpload = async event => {
    const file = event.target.files?.[0];
    if (!file || !user?.email || isMilpersLoading) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email', user.email);
    if (milpersJobPollRef.current) {
      window.clearTimeout(milpersJobPollRef.current);
      milpersJobPollRef.current = null;
    }
    setIsMilpersLoading(true);
    setMilpersUploadProgress(0);
    setMilpersProcessingPhase(`Uploading ${file.name || 'MILPERS roster'}...`);
    setMilpersImportJob(null);
    setMilpersMessage('');
    setMilpersError('');
    setMilpersSummary(null);

    try {
      const res = await api.post('/personnel/milpers/import-jobs', formData, {
        onUploadProgress: progressEvent => {
          if (!progressEvent.total) return;
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setMilpersUploadProgress(percent);
          if (percent >= 100) {
            setMilpersProcessingPhase('Upload complete. Waiting for backend processing to start...');
          }
        },
      });
      const job = res.data?.job;
      if (!job?.id) {
        throw new Error('MILPERS import started but did not return a job id.');
      }
      setMilpersImportJob(job);
      setMilpersUploadProgress(null);
      setMilpersProcessingPhase(job.stage || 'Queued for backend processing...');
      await pollMilpersJob(job.id);
    } catch (err) {
      const payload = err.response?.data || {};
      const isNetworkError = !err.response && String(err.message || '').toLowerCase().includes('network');
      markMilpersJobFailed(
        payload.message ||
        (isNetworkError
          ? 'The browser lost connection during the MILPERS upload. No import job was confirmed, so the roster was not applied from this request. Try again after the backend task is healthy.'
          : err.message) ||
        'MILPERS import failed',
        payload
      );
    }
  };

  const saveMilpersLocationMapping = async () => {
    setMilpersError('');
    setMilpersMessage('');
    try {
      const res = await api.post('/personnel/milpers/location-mappings', {
        email: user.email,
        dutyLocationCode: milpersLocationDraft.dutyLocationCode,
        location: milpersLocationDraft.location,
      });
      setMilpersMessage(res.data?.message || 'MILPERS location mapping saved.');
      updateMilpersMappingState(res.data);
      setMilpersLocationDraft({ dutyLocationCode: '', location: '' });
    } catch (err) {
      setMilpersError(err.response?.data?.message || 'Unable to save MILPERS location mapping');
    }
  };

  const deleteMilpersLocationMapping = async mappingId => {
    setMilpersError('');
    setMilpersMessage('');
    try {
      const res = await api.delete(`/personnel/milpers/location-mappings/${mappingId}`, {
        data: { email: user.email },
      });
      setMilpersMessage(res.data?.message || 'MILPERS location mapping removed.');
      updateMilpersMappingState(res.data);
    } catch (err) {
      setMilpersError(err.response?.data?.message || 'Unable to remove MILPERS location mapping');
    }
  };

  const saveMilpersDafscMapping = async () => {
    const dafscs = Array.from(new Set([
      ...milpersDafscSelection,
      milpersManualDafsc,
    ].map(value => String(value || '').trim().toUpperCase()).filter(Boolean)));
    setMilpersError('');
    setMilpersMessage('');
    try {
      const res = await api.post('/personnel/milpers/dafsc-shop-mappings', {
        email: user.email,
        dafscs,
        shopName: milpersShopName,
      });
      setMilpersMessage(res.data?.message || 'MILPERS DAFSC mapping saved.');
      updateMilpersMappingState(res.data);
      setMilpersDafscSelection([]);
      setMilpersManualDafsc('');
      setMilpersShopName('');
    } catch (err) {
      setMilpersError(err.response?.data?.message || 'Unable to save MILPERS DAFSC mapping');
    }
  };

  const deleteMilpersDafscMapping = async mappingId => {
    setMilpersError('');
    setMilpersMessage('');
    try {
      const res = await api.delete(`/personnel/milpers/dafsc-shop-mappings/${mappingId}`, {
        data: { email: user.email },
      });
      setMilpersMessage(res.data?.message || 'MILPERS DAFSC mapping removed.');
      updateMilpersMappingState(res.data);
    } catch (err) {
      setMilpersError(err.response?.data?.message || 'Unable to remove MILPERS DAFSC mapping');
    }
  };

  const milpersUnmappedDutyLocationRows = Object.entries(milpersSummary?.unmappedDutyLocations || {});
  const milpersUnmappedDafscRows = Object.entries(milpersSummary?.unmappedDafscs || {});
  const milpersPartialReviewMessage = milpersSummary?.blocked && Number((milpersSummary.updated || 0) + (milpersSummary.created || 0)) > 0
    ? `${milpersSummary.blocked} candidate row${milpersSummary.blocked === 1 ? '' : 's'} still need mapping or correction. Valid rows from this import were applied.`
    : '';
  const milpersDafscOptions = Array.from(new Set([
    ...milpersUnmappedDafscRows.map(([dafsc]) => dafsc),
    ...(milpersMappings.unmappedPersonnelDafscs || []),
    ...(milpersMappings.dafscMappings || []).map(mapping => mapping.dafsc),
    milpersManualDafsc,
  ].filter(value => value && value !== '(blank)'))).sort();
  const selectedMilpersJobId = milpersImportJob?.id || '';
  const normalizeMilpersDafscChip = useCallback(value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, ''), []);
  const mappedMilpersDafscCodes = useMemo(() => new Set(
    (milpersMappings.dafscMappings || [])
      .map(mapping => normalizeMilpersDafscChip(mapping.dafsc))
      .filter(Boolean)
  ), [normalizeMilpersDafscChip, milpersMappings.dafscMappings]);
  const visibleMilpersImportJobs = useMemo(() => {
    const jobs = Array.isArray(milpersImportJobs) ? milpersImportJobs : [];
    const latestJobs = jobs.slice(0, 4);
    if (!selectedMilpersJobId || latestJobs.some(job => job.id === selectedMilpersJobId)) {
      return latestJobs;
    }
    const selectedJob = jobs.find(job => job.id === selectedMilpersJobId);
    return selectedJob ? [selectedJob, ...latestJobs.slice(0, 3)] : latestJobs;
  }, [milpersImportJobs, selectedMilpersJobId]);

  const formatMilpersJobTimestamp = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMilpersHistorySelect = job => {
    if (!job) return;
    if (milpersJobPollRef.current) {
      window.clearTimeout(milpersJobPollRef.current);
      milpersJobPollRef.current = null;
    }
    const status = String(job.status || '').toLowerCase();
    showMilpersJobResult(job);
    if (['queued', 'processing', 'running'].includes(status)) {
      setIsMilpersLoading(true);
      setMilpersUploadProgress(null);
      setMilpersProcessingPhase(job.stage || 'Server is parsing, matching, and applying the roster...');
      pollMilpersJob(job.id);
    }
  };

  // modified upload handler for personnel files
  const handleUpload = async (e, type) => {
    console.log("▶︎ handleUpload called:", type, e.target.files[0]);  
    const file = e.target.files[0];
    if (!file) return;
    if (type === "personnel") {
      const confirmed = window.confirm(
        "Uploading this file will replace the entire Personnel roster for the selected location. Rows not included in the file will be removed. Continue?"
      );
      if (!confirmed) {
        e.target.value = null;
        return;
      }
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("email", user.email);
    if (type === "personnel") {
      formData.append("location", selectedBase);
    }
    try {
      const res = await api.post(`/upload-${type}`, formData);
      // show success in ribbon banner
      setNotification(res.data.message);             
      // 2) Push any server-sent alerts into the bell-badge
      if (res.data.alerts) {
        res.data.alerts.forEach(alert => {
          setAlerts(prev => [
            {
              id:      alert.id,
              message: `[${alert.tail}] ${alert.action}: ${alert.new_value}`
            },
            ...prev
          ]);       
        });
      }    
      // 3) Refresh table data
      await fetchData(type);
      // clear banner after 3s
      setTimeout(() => setNotification(""), 3000);      
      // redundant Clear input value to allow re-uploading the same file later
      defaultPersonnelRef.current.value = "";
    } catch (err) {
          const message = err.response?.data?.message || err.response?.data?.error || err.message;
          console.error("Upload failed:", message);
          alert("Server error: " + message);
    };
  };

  const handleDownloadPersonnelTemplate = async () => {
    try {
      const res = await api.get('/download-personnel-template', {
        params: { email: user.email },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'mfe_personnel_upload_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download personnel template', err);
      alert(err.response?.data?.message || 'Unable to download personnel template');
    }
  };

  const handleDownloadPersonnelRoster = async () => {
    try {
      const res = await api.get('/download-personnel-roster', {
        params: { email: user.email, location: selectedBase, global: globalView },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'mfe_personnel_roster.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download personnel roster', err);
      alert(err.response?.data?.message || 'Unable to download personnel roster');
    }
  };
    
  const handlePersonnelToggle = (rowIndex, fieldKey) => {
    setPersonnelData(prev => {
      const newData = [...prev];
      // flip between "Yes" and "No"
      newData[rowIndex] = {
        ...newData[rowIndex],
        [fieldKey]: newData[rowIndex][fieldKey] === "Yes" ? "No" : "Yes"
      };
      return newData;
    });
  };
  
  // re-fetch issues once on login and whenever the base/location changes
  useEffect(() => {
    if (canFetchOperationalData()) {
        fetchData("personnel");
        fetchIssues();
    }
  }, [user, selectedBase, globalView]);

  // for periodic refresh (every 60 seconds)
  useEffect(() => {
    if (!canFetchOperationalData()) return; // only refresh operational data when allowed

    const interval = setInterval(() => {
      fetchData("personnel");
      fetchIssues();
    }, 60000); // <-- Refresh interval (milliseconds). 60000ms = 1 minute.

    return () => clearInterval(interval); // cleanup interval on component unmount
  }, [user, selectedBase, globalView]); // dependencies to restart timer if these change
  


  // Requests location sharing after login and keeps it fresh while the session is active.
  useEffect(() => {
    if (!user) return;

    if (!navigator.geolocation) {
      setLocationSharingNotice({
        severity: 'warning',
        title: 'Location sharing is not available',
        body: 'This browser or device does not support location sharing, so the live personnel map will not show your position.',
      });
      return;
    }

    let cancelled = false;
    let permissionStatus = null;
    let geoErrorShown = false;

    const postLocation = pos => {
      api.post("/update-user-location", {
        email: user.email,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      })
        .then(() => {
          window.dispatchEvent(new CustomEvent("mfe:user-location-updated"));
          if (!cancelled) {
            setLocationSharingNotice(null);
          }
        })
        .catch(console.error);
    };

    const handleGeoError = err => {
      if (cancelled || geoErrorShown) return;
      geoErrorShown = true;
      console.error("Geo error:", err);
      if (err?.code === 1) {
        setLocationSharingNotice({
          severity: 'warning',
          title: 'Location sharing is blocked',
          body: 'Chrome will not show the location prompt again while this site is blocked. Tap the site controls icon next to the address, open Permissions or Site settings, set Location to Allow or Ask, then try again.',
          actionLabel: 'Try Again',
        });
      } else if (err?.code === 2) {
        setLocationSharingNotice({
          severity: 'warning',
          title: 'Location could not be determined',
          body: 'Your browser allowed the request, but the device could not provide a location fix. Check device location services and try again.',
          actionLabel: 'Try Again',
        });
      } else if (err?.code === 3) {
        setLocationSharingNotice({
          severity: 'warning',
          title: 'Location request timed out',
          body: 'MFE could not get a location fix before the browser timeout. Move to a better signal area or check device location services, then try again.',
          actionLabel: 'Try Again',
        });
      } else {
        setLocationSharingNotice({
          severity: 'warning',
          title: 'Location sharing needs attention',
          body: 'MFE could not start browser location sharing. Check site permissions and device location services, then try again.',
          actionLabel: 'Try Again',
        });
      }
    };

    const locationOptions = {
      enableHighAccuracy: true,
      maximumAge: 1000 * 60 * 2,
      timeout: 10000,
    };

    const startLocationWatch = () => {
      navigator.geolocation.getCurrentPosition(
        postLocation,
        handleGeoError,
        locationOptions
      );

      return navigator.geolocation.watchPosition(
        postLocation,
        handleGeoError,
        locationOptions
      );
    };

    let watcher = startLocationWatch();

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(status => {
          permissionStatus = status;
          if (!cancelled && status.state === 'denied') {
            setLocationSharingNotice({
              severity: 'warning',
              title: 'Location sharing is blocked',
              body: 'Chrome has this site set to block location. MFE cannot force the browser prompt open until you change the site permission to Allow or Ask, then use Try Again.',
              actionLabel: 'Try Again',
            });
          }
          status.onchange = () => {
            if (cancelled) return;
            geoErrorShown = false;
            setLocationSharingNotice(null);
            if (watcher) navigator.geolocation.clearWatch(watcher);
            watcher = startLocationWatch();
          };
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      if (permissionStatus) permissionStatus.onchange = null;
      if (watcher) navigator.geolocation.clearWatch(watcher);
    };
  }, [user, api, locationSharingRetryNonce]);     

  const jumpToOnboardingTab = tab => {
    if (!tab) return;
    setActiveTab(tab);
  };

  const isMobileOnboardingViewport = () => (
    (window.visualViewport?.width || window.innerWidth || 1024) <= 640
  );

  const getOnboardingScrollableAncestor = (element, axis) => {
    let node = element?.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflow = axis === 'x'
        ? `${style.overflowX} ${style.overflow}`
        : `${style.overflowY} ${style.overflow}`;
      const hasScroll = axis === 'x'
        ? node.scrollWidth > node.clientWidth + 4
        : node.scrollHeight > node.clientHeight + 4;
      if (hasScroll && /(auto|scroll|overlay)/.test(overflow)) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  const scrollOnboardingTargetIntoView = target => {
    if (!target) return;
    if (!isMobileOnboardingViewport()) {
      target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
      return;
    }

    const horizontalScroller = getOnboardingScrollableAncestor(target, 'x');
    if (horizontalScroller) {
      const scrollerRect = horizontalScroller.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const desiredLeft = Math.max(12, Math.round(scrollerRect.width * 0.18));
      const nextLeft = horizontalScroller.scrollLeft + targetRect.left - scrollerRect.left - desiredLeft;
      horizontalScroller.scrollTo({
        left: Math.max(0, nextLeft),
        behavior: 'smooth',
      });
    } else {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    const rect = target.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 768;
    const desiredTop = Math.max(76, Math.min(132, Math.round(viewportHeight * 0.18)));
    const bottomGuard = Math.round(viewportHeight * 0.46);
    if (rect.top < desiredTop || rect.bottom > viewportHeight - bottomGuard) {
      window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - desiredTop),
        behavior: 'smooth',
      });
    }
  };

  const measureOnboardingTarget = target => {
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setOnboardingTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right,
    });
  };

  const updateOnboardingTargetRect = (selector, options = {}) => {
    if (!selector) {
      setOnboardingTargetRect(null);
      return;
    }
    const target = document.querySelector(selector);
    if (!target) {
      setOnboardingTargetRect(null);
      if (options.scroll && !options.retry) {
        window.setTimeout(() => updateOnboardingTargetRect(selector, { ...options, retry: true }), 320);
      }
      return;
    }
    if (options.scroll) {
      scrollOnboardingTargetIntoView(target);
    }
    const delay = options.scroll && isMobileOnboardingViewport() ? 360 : 180;
    window.setTimeout(() => measureOnboardingTarget(target), delay);
    if (options.scroll && isMobileOnboardingViewport()) {
      window.setTimeout(() => measureOnboardingTarget(target), 680);
    }
  };
  const currentOnboardingStep = ONBOARDING_STEPS[onboardingStep] || ONBOARDING_STEPS[0];
  useEffect(() => {
    if (!user || !showOnboarding || !currentOnboardingStep) return undefined;
    if (currentOnboardingStep.targetTab) {
      setActiveTab(currentOnboardingStep.targetTab);
    }
    const refresh = event => {
      if (event?.target instanceof Element && event.target.closest('.onboarding-modal')) return;
      updateOnboardingTargetRect(currentOnboardingStep.targetSelector);
    };
    const timeout = window.setTimeout(() => {
      updateOnboardingTargetRect(currentOnboardingStep.targetSelector, { scroll: true });
    }, 120);
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    window.visualViewport?.addEventListener('resize', refresh);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
      window.visualViewport?.removeEventListener('resize', refresh);
    };
  }, [user, showOnboarding, onboardingStep, currentOnboardingStep]);
  const getOnboardingCalloutStyle = () => {
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 1024;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 768;
    return getOnboardingCalloutLayout(viewportWidth, viewportHeight, onboardingTargetRect);
  };
  const onboardingCallout = getOnboardingCalloutStyle();
  const getOnboardingSpotlightStyle = () => {
    if (!onboardingTargetRect) return null;
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 1024;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 768;
    const inset = 8;
    const left = Math.max(inset, Math.min(onboardingTargetRect.left - inset, viewportWidth - 36));
    const top = Math.max(inset, Math.min(onboardingTargetRect.top - inset, viewportHeight - 36));
    return {
      top: `${top}px`,
      left: `${left}px`,
      width: `${Math.max(20, Math.min(onboardingTargetRect.width + 16, viewportWidth - left - inset))}px`,
      height: `${Math.max(20, Math.min(onboardingTargetRect.height + 16, viewportHeight - top - inset))}px`,
    };
  };
  const onboardingSpotlightStyle = getOnboardingSpotlightStyle();
  const isCurrentStepOpenTabButton = Boolean(
    currentOnboardingStep?.targetTab &&
    activeTab === currentOnboardingStep.targetTab &&
    currentOnboardingStep.targetSelector?.startsWith('[data-tour-target="tab-')
  );
  const pendingLocationTransfer = locationTransferRequests.find(row => row.status === 'pending');
  const transferHistory = locationTransferRequests.filter(row => row.status !== 'pending').slice(0, 3);


  if (!user && isSessionRestorePending) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__intro">
            <a className="auth-card__logo-link" href="https://mfe.llc" aria-label="Mission Focused Ethos home">
              <img src="/mfe-logo-transparent.png" alt="MFE" className="auth-card__logo" />
            </a>
            <p className="auth-card__eyebrow">Restoring session...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <AuthConsentGate required={requiresAuthConsent(window.location.hostname, authConfig.appEdition)}>
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__intro">
            <a className="auth-card__logo-link" href="https://mfe.llc" aria-label="Mission Focused Ethos home">
              <img src="/mfe-logo-transparent.png" alt="MFE" className="auth-card__logo" />
            </a>
            <p className="auth-card__eyebrow">Flightline Operations Portal</p>
          </div>
          <div className="auth-card__body">
            <div className="auth-primary-panel">
              <div className="auth-primary-panel__content">
                {isResettingPassword ? (
                <>
            <h3>Reset Password</h3>
            <input
              placeholder="Email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (!isLogin) {
                  setIsCodeSent(false);
                  setIsCodeVerified(false);
                  setRegCode('');
                }
              }}
            /><br />
            {!isCodeStep ? (
              <>
                <button onClick={async () => {
                  try {
                    await api.post('/request-password-reset', { email: email.trim().toLowerCase() });
                    alert('Reset code sent.');
                    setResetCode('');
                    setIsCodeStep(true); // go to code/password step
                  } catch (err) {
                    alert('Error sending code: ' + (err.response?.data?.message || err.message));
                  }
                }}>
                  Send Reset Code
                </button>
                <button onClick={() => {
                  setIsResettingPassword(false);
                  setEmail('');
                  setResetCode('');
                  setNewPassword('');
                  setIsCodeStep(false);
                  setConfirmPassword('');
                }}>Cancel</button>
              </>
            ) : (
              <>
                <input
                  placeholder="Reset Code"
                  value={resetCode}
                  onChange={e => setResetCode(e.target.value)}
                /><br />
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                /><br />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                /><br />
                <p className="auth-password-requirements">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
                <button onClick={async () => {
                  if (newPassword !== confirmPassword) {
                    alert('Passwords do not match.');
                    return;
                  }

                  const passwordError = getPasswordStrengthError(newPassword, email);
                  if (passwordError) {
                    alert(passwordError);
                    return;
                  }

                  try {
                    await api.post('/verify-password-reset', {
                      email: email.trim().toLowerCase(),
                      code: resetCode.trim(),
                      newPassword,
                      confirmPassword
                    });
                    alert('Password reset.');
                    setIsResettingPassword(false);
                    setEmail('');
                    setResetCode('');
                    setNewPassword('');
                    setIsCodeStep(false);
                    setConfirmPassword('');
                  } catch (err) {
                    alert('Error resetting password: ' + (err.response?.data?.message || err.message));
                  }
                }}>Reset Password</button>
                <button onClick={async () => {
                  try {
                    await api.post('/request-password-reset', { email: email.trim().toLowerCase() });
                    alert('New reset code sent.');
                    setResetCode('');
                  } catch (err) {
                    alert('Error sending code: ' + (err.response?.data?.message || err.message));
                  }
                }}>
                  Resend Reset Code
                </button>
                <button onClick={() => {
                  setIsResettingPassword(false);
                  setEmail('');
                  setResetCode('');
                  setNewPassword('');
                  setIsCodeStep(false);
                  setConfirmPassword('');
                }}>Cancel</button>
              </>
            )}
                  </>
                ) : (
                  <>
            <input
              placeholder="Email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (!isLogin) {
                  resetRegistrationVerificationState();
                }
              }}
              onBlur={() => {
                if (!isLogin && userType) {
                  requestRegistrationCode(email, userType);
                }
              }}
            /><br />
            <input
              key={`auth-password-${authSecretClearNonce}-${isLogin ? 'login' : 'register'}`}
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={isLogin ? 'current-password' : 'new-password'}
            /><br />
            {!isLogin && (
              <>
                <input
                  key={`auth-confirm-password-${authSecretClearNonce}`}
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                /><br />
                <p className="auth-password-requirements">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
              </>
            )}
  
            {!isLogin && (
              <>
                <label>User Type:</label><br />
                <select value={userType} onChange={async e => {
                  const nextUserType = e.target.value;
                  setUserType(nextUserType);
                  resetRegistrationVerificationState();
                  setRole('');
                  setShop('');
                  setCountry('');
                  setState('');
                  setBase('');
                  setRegistrationLocationAdminStatus(null);
                  setCommercialRegistration(initialCommercialRegistration);
                  if (email.trim() && nextUserType && nextUserType !== 'Commercial') {
                    await requestRegistrationCode(email, nextUserType);
                  }
                }}>
                  <option value="">Select User Type</option>
                  {userTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {userTypes.length === 0 ? (
                  <p style={{ marginTop: 8, color: '#7b2f2f' }}>
                    Registration is not enabled for this deployment.
                  </p>
                ) : null}
                {authConfig.militaryRegistrationEnabled ? (
                  <p className="registration-code-help">
                    Register with your valid military email address. CAC / PIV login can be linked from Profile after your first password login.
                  </p>
                ) : null}
  
                {Boolean(userType) && (
                  <>
                    {(userType === 'USAF' || userType === 'USArmy') && (
                      <p style={{ marginTop: 8 }}>
                        {userType} users must use an approved domain:
                        {userType === 'USAF'
                          ? ' @us.af.mil or @mail.mil'
                          : ' @usa.army.mil, @army.mil, or @mail.mil'}
                      </p>
                    )}
                    {userType === 'Commercial' && (
                      <CommercialRegistrationFields
                        api={api}
                        form={commercialRegistration}
                        onFormChange={updateCommercialRegistration}
                      />
                    )}
                    {!isCodeSent ? (
                      <>
                        <p style={{ marginTop: 8, color: '#4d657d' }}>
                          {isSendingRegistrationCode
                            ? 'Sending verification code...'
                            : userType === 'Commercial'
                              ? commercialCodeNeedsResend
                                ? 'Your commercial signup details changed. Send a fresh verification code for the updated company information.'
                                : 'A verification code will be sent automatically once your commercial signup details are valid.'
                              : 'A verification code will be sent automatically after you enter a valid registration email.'}
                        </p>
                        <p className="registration-code-help">
                          If the code does not arrive, check your spam/junk folder. Air Force email filters can delay delivery; contact info@mfe.llc if you need help getting the token.
                        </p>
                        {userType === 'Commercial' && isCommercialRegistrationReady(commercialRegistration) && (commercialCodeNeedsResend || Boolean(lastCommercialCodeAttemptKey)) ? (
                          <button
                            type="button"
                            onClick={() => requestRegistrationCode(email, userType, { forceResend: true })}
                            disabled={isSendingRegistrationCode}
                          >
                            {isSendingRegistrationCode ? 'Sending...' : 'Send Verification Code'}
                          </button>
                        ) : null}
                      </>
                    ) : !isCodeVerified ? (
                      <>
                        <p>Enter your 6-digit code:</p>
                        <p className="registration-code-help">
                          Not seeing it yet? Check spam/junk first. Air Force firewall filtering can delay delivery, and info@mfe.llc can help if the token does not come through.
                        </p>
                        <input value={regCode} onChange={e => setRegCode(e.target.value)} />
                        <button onClick={async () => {
                          try {
                            const res = await api.post('/verify-registration-code', {
                              email: email.trim().toLowerCase(),
                              userType,
                              code: regCode,
                              ...(userType === 'Commercial' ? buildCommercialRegistrationPayload(commercialRegistration) : {})
                            });
                            if (res.data.verified) {
                              if (userType === 'Commercial') {
                                if (password !== confirmPassword) {
                                  alert('Passwords do not match.');
                                  return;
                                }
                                await completeRegistration();
                                return;
                              }
                              setIsCodeVerified(true);
                            } else if (res.data.context_mismatch) {
                              setIsCodeSent(false);
                              setRegCode('');
                              setCommercialCodeNeedsResend(true);
                              alert('Your registration details changed after the code was issued. Please send a new verification code for the updated signup information.');
                            } else if (res.data.code_expired) {
                              setIsCodeSent(false);
                              setRegCode('');
                              alert('Code expired. Please request a new one.');
                            } else {
                              alert('Code incorrect.');
                            }
                          } catch (err) {
                            alert('Error verifying code: ' + (err.response?.data?.message || err.message));
                          }
                        }}>Verify Code</button>
                        <button
                          type="button"
                          onClick={() => requestRegistrationCode(email, userType, { forceResend: true })}
                          disabled={isSendingRegistrationCode}
                        >
                          {isSendingRegistrationCode ? 'Sending...' : 'Resend Code'}
                        </button>
                      </>
                    ) : (
                      <p style={{ color: 'green' }}>Email verified!</p>
                    )}
                  </>
                )}
  
                {Boolean(userType) && isCodeVerified ? (
                  <>
                    {(userType === 'USAF' || userType === 'USArmy') && (
                      <>
                        <select value={country} onChange={e => { setCountry(e.target.value); setState(''); setBase(''); }}>
                          <option value="">Select Country</option>
                          {Object.keys(baseLocationTree).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {country && (
                          <select value={state} onChange={e => { setState(e.target.value); setBase(''); }}>
                            <option value="">Select State</option>
                            {Object.keys(baseLocationTree[country] || {}).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                        {state && (
                          <select value={base} onChange={e => setBase(e.target.value)}>
                            <option value="">Select Base</option>
                            {(baseLocationTree[country]?.[state] || []).map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        )}
                      </>
                    )}
                    {(userType === 'USAF' || userType === 'USArmy') && (
                      <>
                        <p className="registration-code-help">
                          Recommended: the first signup for a location should select siteAdmin only if they are the designated unit administrator.
                          siteAdmins manage users, roles, support access, and location settings.
                        </p>
                        {base ? (
                          <p className="registration-code-help">
                            {isLoadingRegistrationAdminStatus
                              ? 'Checking whether this location already has a siteAdmin...'
                              : registrationLocationAdminStatus?.message || 'Choose your normal operational role. If this location has no active siteAdmin, the first active user can establish siteAdmin access.'}
                          </p>
                        ) : null}
                        <label>Role:</label><br />
                        <select value={role} onChange={e => setRole(e.target.value)}>
                          <option value="">Select Role</option>
                          {usafRoleOptions
                            .map(r => <option key={r} value={r}>{r}</option>)}
                        </select><br />
                        {role === 'siteAdmin' && registrationLocationAdminStatus?.hasActiveSiteAdmin ? (
                          <p className="registration-code-help" style={{ color: '#7b2f2f' }}>
                            This location already has a siteAdmin. Register with your normal role and ask {registrationLocationAdminStatus.siteAdminContacts || 'your siteAdmin'} to update access if needed.
                          </p>
                        ) : null}
                        <label>Shop:</label><br />
                        <select value={shop} onChange={e => setShop(e.target.value)}>
                          <option value="">Select Shop</option>
                          {usafShopOptions
                            .map(s => <option key={s} value={s}>{s}</option>)}
                        </select><br />
                      </>
                    )}
                  </>
                ) : null}
              </>
            )}

            {!isLogin && (
              <label className="auth-terms">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={event => setTermsAccepted(event.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <a href={CURRENT_TERMS_URL} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>
                    MFE Terms and Conditions / End User License Agreement
                  </a>
                  <strong> version {CURRENT_TERMS_VERSION}</strong>.
                </span>
              </label>
            )}
  
            {isAuthSubmitting && (
              <p className="auth-card__status" aria-live="polite">
                {isLogin ? 'Logging in...' : 'Completing registration...'}
              </p>
            )}
            {(!isLogin || authConfig.passwordLoginEnabled) ? (
              <button
                onClick={handleAuth}
                disabled={
                  isAuthSubmitting
                  || (
                    !isLogin
                    && isCodeVerified
                    && (userType === 'USAF' || userType === 'USArmy')
                    && (!base || !role || !shop)
                  )
                }
              >
                {isAuthSubmitting
                  ? (isLogin ? "Logging in..." : "Registering...")
                  : (isLogin ? "Login" : "Register")}
              </button>
            ) : (
              <p className="auth-card__status" aria-live="polite">
                Password login is disabled for this deployment. Use CAC / SSO.
              </p>
            )}
            <button onClick={() => {
              setIsLogin(!isLogin);
              resetRegistrationVerificationState();
              setConfirmPassword('');
              setTermsAccepted(false);
              setCommercialRegistration(initialCommercialRegistration);
            }} disabled={isAuthSubmitting}>Switch to {isLogin ? "Register" : "Login"}</button>
            {isLogin && (
              <button onClick={() => {
                setIsResettingPassword(true);
                setEmail('');
                setResetCode('');
                setNewPassword('');
                setIsCodeStep(false);
                setConfirmPassword('');
              }} disabled={isAuthSubmitting}>Forgot Password?</button>
            )}
            {!isResettingPassword ? (
              <p className="auth-primary-panel__note">
                First-time users must register with a us.af.mil or mail.mil email and strong password.
              </p>
            ) : null}
                  </>
                )}
              </div>
            </div>
            {!isResettingPassword && isLogin && authConfig.cacLoginEnabled ? (
              <aside className="auth-cac-panel" aria-label="CAC PIV login">
                <div className="auth-cac-panel__divider" aria-hidden="true" />
                <div className="auth-cac-panel__content">
                  <span className="auth-cac-panel__label">CAC / PIV</span>
                  <p className="auth-cac-panel__note">
                    After first login, open Profile to link CAC / PIV for future login.
                  </p>
                  <button type="button" onClick={handleCacLogin} disabled={isAuthSubmitting}>
                    {isAuthSubmitting ? 'Checking CAC / SSO...' : 'Login With CAC / PIV'}
                  </button>
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </div>
      </AuthConsentGate>
    );
  }

  const canAccessOperationalData = canUserAccessOperationalData(user);
  const isSupportUser = Boolean(user?.is_support_user);
  const supportLocations = user?.accessible_support_locations || [];
  const supportUserHasLocations = !isSupportUser || supportLocations.length > 0;
  const usesOperationalShell = user.userType === 'USAF' || isSupportUser;
  const launchSuccessAdvisorEnabled = Boolean(headerSubscription?.integrationFlags?.launch_success_advisor);
  const visiblePlatformAnnouncements = platformAnnouncements.filter(item => (
    item?.id && !dismissedAnnouncementIds.includes(item.id)
  ));
  const weatherBanner = weatherSummary?.assessment?.banner?.active
    ? weatherSummary.assessment.banner
    : null;
  const weatherBannerKey = weatherBanner
    ? `${weatherSummary?.location || selectedBase}|${weatherSummary?.cache?.fetchedAt || weatherSummary?.generatedAt || weatherBanner.title}`
    : '';
  const showWeatherBanner = Boolean(weatherBanner && weatherBannerKey !== dismissedWeatherBannerKey);
    
  return (
        user.userType === 'Commercial' ? (
          <div className="app-shell">
            <main className="app-main-panel">
              <CommercialApp api={api} user={user} handleLogout={handleLogout} />
            </main>
          </div>
        ) : (
        usesOperationalShell && (
          <div className="app-shell">
            <Header
              user={user}
              selectedBase={selectedBase}
              setSelectedBase={setSelectedBase}
              locationTree={baseLocationTree}
              locationsLoading={baseLocationsLoading}
              locationsError={baseLocationsError}
              onRetryLocations={reloadBaseLocations}
              globalView={globalView}
              setGlobalView={setGlobalView}
              handleLogout={handleLogout}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              subscription={headerSubscription}
              alerts={alerts}
              clearAlerts={clearAlerts}
              onOpenProfile={openProfileSettings}
            />
            {visiblePlatformAnnouncements.length > 0 ? (
              <div className="platform-announcement-stack" aria-live="polite">
                {visiblePlatformAnnouncements.map(item => (
                  <section key={item.id} className={`platform-announcement is-${item.severity || 'info'}`}>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.body}</p>
                    </div>
                    <button type="button" onClick={() => dismissPlatformAnnouncement(item.id)}>
                      Dismiss
                    </button>
                  </section>
                ))}
              </div>
            ) : null}
            {showWeatherBanner ? (
              <div className="platform-announcement-stack" aria-live="polite">
                <section className={`platform-announcement weather-advisory-banner is-${weatherBanner.severity || 'warning'}`}>
                  <div>
                    <strong>{weatherBanner.title}</strong>
                    <p>{weatherBanner.body}</p>
                  </div>
                  <div className="location-sharing-notice__actions">
                    <button type="button" onClick={() => setActiveTab('weather')}>
                      Open Weather
                    </button>
                    <button type="button" onClick={() => setDismissedWeatherBannerKey(weatherBannerKey)}>
                      Dismiss
                    </button>
                  </div>
                </section>
              </div>
            ) : null}
            {locationSharingNotice && !locationSharingNoticeDismissed ? (
              <div className="platform-announcement-stack" aria-live="polite">
                <section className={`platform-announcement location-sharing-notice is-${locationSharingNotice.severity || 'warning'}`}>
                  <div>
                    <strong>{locationSharingNotice.title}</strong>
                    <p>{locationSharingNotice.body}</p>
                  </div>
                  <div className="location-sharing-notice__actions">
                    {locationSharingNotice.actionLabel ? (
                      <button type="button" onClick={retryLocationSharing}>
                        {locationSharingNotice.actionLabel}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => setLocationSharingNoticeDismissed(true)}>
                      Dismiss
                    </button>
                  </div>
                </section>
              </div>
            ) : null}
            {isProfileOpen && (
              <div className="profile-modal-backdrop" role="presentation">
                <section className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-settings-title">
                  <div className="profile-modal__header">
                    <div>
                      <span className="profile-modal__eyebrow">User Profile</span>
                      <h3 id="profile-settings-title">Preferences</h3>
                      <p>{user.email}</p>
                    </div>
                    <button type="button" className="profile-modal__close" onClick={() => setIsProfileOpen(false)}>
                      Close
                    </button>
                  </div>

                  {profileMessage ? <div className="profile-modal__message is-success">{profileMessage}</div> : null}
                  {profileError ? <div className="profile-modal__message is-error">{profileError}</div> : null}

                  <div className="profile-modal__section">
                    <h4>Display</h4>
                    <label className="profile-modal__field">
                      Theme
                      <select
                        value={profileDraft.theme}
                        onChange={event => updateProfileDraft('theme', event.target.value)}
                      >
                        <option value="system">Use device setting</option>
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                      </select>
                    </label>
                  </div>

                  {authConfig.cacLoginEnabled ? (
                    <div className="profile-modal__section">
                      <h4>CAC / PIV Login</h4>
                      <p>
                        {user.cacLinked
                          ? `Linked certificate: ${user.cacIdentityLabel || 'CAC / PIV certificate'}`
                          : 'Link your verified CAC / PIV certificate to this MFE account after password login.'}
                      </p>
                      {user.cacLinked ? (
                        <span className="profile-cac-linked-status">
                          CAC / PIV is enabled for future login.
                        </span>
                      ) : null}
                      <label className="profile-modal__field">
                        Current Password
                        <input
                          type="password"
                          value={cacLinkPassword}
                          onChange={event => setCacLinkPassword(event.target.value)}
                          autoComplete="current-password"
                          placeholder="Confirm password before linking"
                        />
                      </label>
                      <div className="profile-cac-link-row">
                        <button
                          type="button"
                          className="profile-modal__primary profile-cac-link-button"
                          onClick={handleCacLink}
                          disabled={isLinkingCac || !cacLinkPassword}
                        >
                          {isLinkingCac ? 'Opening CAC / PIV...' : (user.cacLinked ? 'Re-link CAC / PIV' : 'Link CAC / PIV')}
                        </button>
                        {user.cacLinked ? (
                          <span className="profile-cac-link-note">
                            For new / replaced cards or certificates.
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {siteAdminClaimStatus?.canClaimSiteAdmin ? (
                    <div className="profile-modal__section">
                      <h4>siteAdmin Recovery</h4>
                      <p>
                        No active siteAdmin is registered for {selectedBase || user.location}. If you are the designated unit administrator,
                        claim siteAdmin access here so your location can manage users and roles.
                      </p>
                      <button
                        type="button"
                        className="profile-modal__primary"
                        onClick={claimSiteAdmin}
                        disabled={isClaimingSiteAdmin}
                      >
                        {isClaimingSiteAdmin ? 'Claiming...' : 'Claim siteAdmin'}
                      </button>
                    </div>
                  ) : null}

                  {canRequestLocationTransfer ? (
                    <div className="profile-modal__section">
                      <h4>Location Transfer</h4>
                      <p>Current default: {user.location || 'Not assigned'}</p>
                      {pendingLocationTransfer ? (
                        <div className="profile-transfer-status">
                          <strong>Pending gaining-siteAdmin approval</strong>
                          <span>{pendingLocationTransfer.fromLocation} to {pendingLocationTransfer.toLocation}</span>
                          <span>{pendingLocationTransfer.requestedRole || 'No role'} / {pendingLocationTransfer.requestedShop || 'No shop'}</span>
                        </div>
                      ) : (
                        <>
                          <div className="profile-transfer-grid">
                            <label className="profile-modal__field">
                              Country / Area
                              <select
                                value={locationTransferDraft.country}
                                onChange={event => updateLocationTransferDraft('country', event.target.value)}
                              >
                                {Object.keys(baseLocationTree).map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            </label>
                            <label className="profile-modal__field">
                              Region
                              <select
                                value={locationTransferDraft.state}
                                onChange={event => updateLocationTransferDraft('state', event.target.value)}
                              >
                                {Object.keys(baseLocationTree[locationTransferDraft.country] || {}).map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            </label>
                            <label className="profile-modal__field">
                              Gaining Location
                              <select
                                value={locationTransferDraft.base}
                                onChange={event => updateLocationTransferDraft('base', event.target.value)}
                              >
                                {(baseLocationTree[locationTransferDraft.country]?.[locationTransferDraft.state] || []).map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            </label>
                            <label className="profile-modal__field">
                              Requested Role
                              <select
                                value={locationTransferDraft.role}
                                onChange={event => updateLocationTransferDraft('role', event.target.value)}
                              >
                                <option value="">No Role</option>
                                {MILITARY_ROLE_OPTIONS.map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            </label>
                            <label className="profile-modal__field">
                              Requested Shop
                              <select
                                value={locationTransferDraft.shop}
                                onChange={event => updateLocationTransferDraft('shop', event.target.value)}
                              >
                                <option value="">No Shop</option>
                                {MILITARY_SHOP_OPTIONS.map(item => (
                                  <option key={item} value={item}>{item}</option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <label className="profile-modal__field">
                            Transfer Note
                            <textarea
                              rows={3}
                              maxLength={1000}
                              value={locationTransferDraft.reason}
                              onChange={event => updateLocationTransferDraft('reason', event.target.value)}
                              placeholder="PCS, deployment, TDY support, or gaining unit context."
                            />
                          </label>
                          <button
                            type="button"
                            className="profile-modal__primary profile-transfer-submit"
                            onClick={submitLocationTransferRequest}
                            disabled={isSubmittingLocationTransfer || !locationTransferDraft.base || locationTransferDraft.base === user.location}
                          >
                            {isSubmittingLocationTransfer ? 'Requesting...' : 'Request Location Transfer'}
                          </button>
                        </>
                      )}
                      {transferHistory.length > 0 ? (
                        <div className="profile-transfer-history">
                          {transferHistory.map(row => (
                            <span key={row.id}>
                              {row.status}: {row.fromLocation} to {row.toLocation}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="profile-modal__section">
                    <h4>Email Notifications</h4>
                    <p>Choose which standard email notices you want to receive. SMS/text alerts are planned for a future release and are not live yet.</p>
                    {[
                      ['launchAssignments', 'Launch assignments'],
                      ['recoveryAssignments', 'Recovery assignments'],
                      ['priorityAssignments', 'Priority task assignments'],
                      ['redballIssues', 'Red Ball issue alerts for my shop or production role'],
                    ].map(([key, label]) => (
                      <label key={key} className="profile-modal__toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(profileDraft.emailNotifications?.[key])}
                          onChange={event => updateProfileDraft(key, event.target.checked)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="profile-modal__section">
                    <h4>SMS Text Alerts (Coming Soon)</h4>
                    <p>SMS/text notifications are not live yet. These preferences are placeholders for future assignment and Red Ball text alerts.</p>
                    {[
                      ['assignmentAlerts', 'Launch, recovery, and priority assignment texts'],
                      ['redballIssues', 'Red Ball issue texts for my shop or production role'],
                    ].map(([key, label]) => (
                      <label key={key} className="profile-modal__toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(profileDraft.smsNotifications?.[key])}
                          onChange={event => updateProfileDraft(`sms:${key}`, event.target.checked)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="profile-modal__section profile-account-access">
                    <h4>Account Access</h4>
                    <p>
                      Deactivate your MFE account access and sign out. Your historical activity will remain in unit records, and a siteAdmin may reactivate your account later.
                    </p>
                    <label className="profile-modal__field">
                      Current Password
                      <input
                        type="password"
                        value={accountClosePassword}
                        onChange={event => setAccountClosePassword(event.target.value)}
                        autoComplete="current-password"
                        placeholder="Confirm password before deactivation"
                      />
                    </label>
                    <label className="profile-modal__field">
                      Type CLOSE to Confirm
                      <input
                        type="text"
                        value={accountCloseConfirm}
                        onChange={event => setAccountCloseConfirm(event.target.value)}
                        autoComplete="off"
                        placeholder="CLOSE"
                      />
                    </label>
                    <button
                      type="button"
                      className="profile-account-access__button"
                      onClick={closeAccountAccess}
                      disabled={isClosingAccount}
                    >
                      {isClosingAccount ? 'Deactivating...' : 'Deactivate My Account'}
                    </button>
                  </div>

                  <div className="profile-modal__actions">
                    <button type="button" className="profile-modal__secondary" onClick={() => setIsProfileOpen(false)}>
                      Cancel
                    </button>
                    <button type="button" className="profile-modal__primary" onClick={saveProfileSettings} disabled={isSavingProfile}>
                      {isSavingProfile ? 'Saving...' : 'Save Preferences'}
                    </button>
                  </div>
                </section>
              </div>
            )}
            {showOnboarding && (
              <div className="profile-modal-backdrop onboarding-backdrop" role="presentation">
                {onboardingSpotlightStyle ? (
                  <div
                    className="onboarding-spotlight"
                    style={onboardingSpotlightStyle}
                    aria-hidden="true"
                  />
                ) : null}
                <section
                  className="onboarding-modal"
                  style={onboardingCallout.style}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="onboarding-title"
                >
                  <span className={`onboarding-modal__arrow ${onboardingCallout.arrowClass}`} aria-hidden="true" />
                  <div className="onboarding-modal__content">
                    <div className="onboarding-modal__topline">
                      <div className="onboarding-modal__progress">
                        {ONBOARDING_STEPS.map((step, index) => (
                          <span key={step.title} className={index <= onboardingStep ? 'is-active' : ''} />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="onboarding-modal__close"
                        onClick={() => finishOnboarding(true)}
                        aria-label="Close first-time tour"
                        title="Close tour"
                      >
                        <X size={18} aria-hidden="true" />
                      </button>
                    </div>
                    <span className="profile-modal__eyebrow">First-Time Tour</span>
                    <h3 id="onboarding-title">{currentOnboardingStep.title}</h3>
                    <p>{currentOnboardingStep.body}</p>
                    <div className="onboarding-modal__tip">{currentOnboardingStep.action}</div>
                    {currentOnboardingStep.targetTab && !isCurrentStepOpenTabButton ? (
                      <button
                        type="button"
                        className="onboarding-modal__target"
                        onClick={() => {
                          jumpToOnboardingTab(currentOnboardingStep.targetTab);
                          window.setTimeout(() => updateOnboardingTargetRect(currentOnboardingStep.targetSelector, { scroll: true }), 120);
                        }}
                      >
                        {currentOnboardingStep.targetLabel || 'Open This Area'}
                      </button>
                    ) : null}
                    <label className="onboarding-modal__dont-show">
                      <input
                        type="checkbox"
                        checked={onboardingDontShowAgain}
                        onChange={event => setOnboardingDontShowAgain(event.target.checked)}
                      />
                      <span>Don&apos;t show this again</span>
                    </label>
                  </div>
                  <div className="profile-modal__actions">
                    <button type="button" className="profile-modal__secondary" onClick={() => finishOnboarding(true)}>
                      Skip
                    </button>
                    {onboardingStep > 0 ? (
                      <button type="button" className="profile-modal__secondary" onClick={() => setOnboardingStep(step => Math.max(0, step - 1))}>
                        Back
                      </button>
                    ) : null}
                    {onboardingStep < ONBOARDING_STEPS.length - 1 ? (
                      <button type="button" className="profile-modal__primary" onClick={() => setOnboardingStep(step => step + 1)}>
                        Next
                      </button>
                    ) : (
                      <button type="button" className="profile-modal__primary" onClick={() => finishOnboarding(false)}>
                        Finish
                      </button>
                    )}
                  </div>
                </section>
              </div>
            )}
            {canAccessOperationalData && supportUserHasLocations && (
            <nav className="app-tabbar">
              <button 
                  className={`tab-button ${activeTab === 'ato' ? 'active' : ''}`}
                  data-tour-target="tab-fly-schedule"
                  onClick={() => setActiveTab("ato")}>Fly Schedule
              </button>
              <button
                  className={`tab-button tab-button--launch-advisor ${activeTab === 'launchAdvisor' ? 'active' : ''}`}
                  onClick={() => setActiveTab("launchAdvisor")}>Launch Advisor
              </button>
              <button 
                  className={`tab-button ${activeTab === 'personnel' ? 'active' : ''}`}
                  data-tour-target="tab-personnel"
                  onClick={() => setActiveTab("personnel")}>Personnel
              </button>
              <button 
                  className={`tab-button ${activeTab === 'issuesLog' ? 'active' : ''}`}
                  data-tour-target="tab-issues-log"
                  onClick={() => setActiveTab("issuesLog")}>Issues Log
              </button>
              <button
                  className={`tab-button ${activeTab === 'priorities' ? 'active' : ''}`}
                  data-tour-target="tab-priorities"
                  onClick={() => setActiveTab("priorities")}>Priorities
              </button>
              <button
                  className={`tab-button ${activeTab === 'status' ? 'active' : ''}`}
                  data-tour-target="tab-status"
                  onClick={() => setActiveTab("status")}>Status
              </button>
              <button
                  className={`tab-button ${activeTab === 'supply' ? 'active' : ''}`}
                  onClick={() => setActiveTab("supply")}>Supply
              </button>
              <button
                  className={`tab-button ${activeTab === 'acftPlans' ? 'active' : ''}`}
                  onClick={() => setActiveTab("acftPlans")}>ACFT Plans
              </button>
              <button
                  className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
                  onClick={() => setActiveTab("calendar")}>Calendar
              </button>
              <button
                  className={`tab-button ${activeTab === 'weather' ? 'active' : ''}`}
                  onClick={() => setActiveTab("weather")}>Weather
              </button>
              <button 
                  className={`tab-button ${activeTab === 'map' ? 'active' : ''}`}
                  onClick={() => setActiveTab("map")}>Map
              </button>
              <button
                  className={`tab-button ${activeTab === 'changeLog' ? 'active' : ''}`}
                  onClick={() => setActiveTab("changeLog")}>Change Log
              </button>
              <button 
                className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
                data-tour-target="tab-reports"
                onClick={() => setActiveTab("reports")}
              >
                Reports
              </button>
              <button 
                className={`tab-button ${activeTab === 'safety' ? 'active' : ''}`}
                onClick={() => setActiveTab("safety")}>Safety Forms
              </button>
              <button
                className={`tab-button ${activeTab === 'lostTool' ? 'active' : ''}`}
                onClick={() => setActiveTab("lostTool")}>Lost Tool
              </button>
              <button
                className={`tab-button ${activeTab === 'help' ? 'active' : ''}`}
                data-tour-target="tab-help"
                onClick={() => setActiveTab("help")}>Help
              </button>
              {canUseCrystalBall ? (
                <button
                  type="button"
                  className={`tab-button tab-button--crystal-ball ${activeTab === 'crystalBall' ? 'active' : ''}`}
                  onClick={() => setActiveTab('crystalBall')}
                >
                  <img className="tab-button__crystal-ball-icon" src="/crystal-ball-nav.png" alt="" aria-hidden="true" />
                  <span>Crystal Ball</span>
                </button>
              ) : null}
            </nav>
            )}
            {siteLoadIndicator ? (
              <div className="app-scope-loading" role="status" aria-live="polite">
                <span className="app-scope-loading__spinner" aria-hidden="true" />
                <div>
                  <strong>Loading {siteLoadIndicator.targetLabel}</strong>
                  <p>
                    {siteLoadIndicator.previousLabel
                      ? `This tab may still be showing ${siteLoadIndicator.previousLabel} until the refresh finishes.`
                      : 'Refreshing this tab for the selected site.'}
                  </p>
                </div>
              </div>
            ) : null}
            <main className="app-main-panel">
              {isSupportUser && !supportUserHasLocations && (
                <section className="support-empty-state">
                  <h2>No Support Access Granted</h2>
                  <p>This support account is active, but no customer location has enabled MFE Support Access yet.</p>
                  <button type="button" onClick={handleLogout}>Log Out</button>
                </section>
              )}
              {supportUserHasLocations && activeTab === "ato" && (
                <ATOscreen
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                  editedCells={editedCells}
                  setEditedCells={setEditedCells}
                  personnelData={personnelData}
                  fetchData={fetchData}
                  alerts={alerts}
                  setAlerts={setAlerts}
                  selectedAtoRow={selectedAtoRow}
                  setSelectedAtoRow={setSelectedAtoRow}
                  issues={issuesData}
                  tableSettings={normalizedUserPreferences.tableSettings.flySchedule}
                  onTableSettingsChange={handleAtoTableSettingsChange}
                />
              )}  
              {supportUserHasLocations && activeTab === "launchAdvisor" && (
                <LaunchAdvisorTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                  isEnabled={launchSuccessAdvisorEnabled}
                  isEntitlementLoading={isHeaderSubscriptionLoading}
                  onOpenUpgrade={() => setActiveTab('upgrade')}
                />
              )}
              {supportUserHasLocations && activeTab === "personnel" && (
                <div>
                  {/* Notification ribbon */}
                  {notification && (
                    <div style={notificationStyle}>
                      {notification}
                    </div>
                  )}
                  <div className="personnel-toolbar">
                    <div className="personnel-toolbar__actions">
                      <button onClick={handleAddPersonnelRow}>
                        + Add Personnel Row
                      </button>
                      {user && 
                      <label className="personnel-upload-button">
                        Choose Upload File
                      <input
                          ref={defaultPersonnelRef}
                          className="personnel-upload-button__input"
                          type="file"
                          accept=".xlsx"
                          onClick={e => e.target.value = null}
                          onChange={e => handleUpload(e, "personnel")}
                      />
                      </label>
                      }
                      <button type="button" onClick={handleDownloadPersonnelTemplate}>
                        Download Personnel Template
                      </button>
                      <button type="button" onClick={handleDownloadPersonnelRoster}>
                        Download Current Roster
                      </button>
                      <div className="personnel-collapse-controls status-field-settings-anchor">
                        <button
                          type="button"
                          className="personnel-collapse-button personnel-collapse-button--customize"
                          onClick={() => setShowPersonnelCollapseSettings(current => !current)}
                        >
                          Customize Collapse
                        </button>
                        <button
                          type="button"
                          className="personnel-collapse-button personnel-collapse-button--toggle"
                          onClick={() => handlePersonnelCollapseSettingsChange(prev => ({ ...prev, collapsed: !prev.collapsed }))}
                        >
                          {personnelCollapseSettings.collapsed ? 'Expand Table' : 'Collapse Table'}
                        </button>
                        {user?.is_superuser ? (
                          <button
                            type="button"
                            className="personnel-collapse-button personnel-collapse-button--milpers"
                            onClick={() => setShowMilpersImportModal(true)}
                          >
                            MILPERS Import
                          </button>
                        ) : null}
                        {showPersonnelCollapseSettings ? (
                          <PersonnelCollapseSettings
                            columns={personnelFieldColumns}
                            hiddenKeys={personnelCollapseSettings.hiddenKeys}
                            onChangeHiddenKeys={hiddenKeysOrUpdater => handlePersonnelCollapseSettingsChange(prev => ({
                              ...prev,
                              hiddenKeys: typeof hiddenKeysOrUpdater === 'function'
                                ? hiddenKeysOrUpdater(prev.hiddenKeys)
                                : hiddenKeysOrUpdater,
                            }))}
                            onClose={() => setShowPersonnelCollapseSettings(false)}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="personnel-filter-bar">
                      <div className="status-field-settings-anchor personnel-field-settings-anchor" ref={personnelFieldSettingsRef}>
                        <button
                          type="button"
                          className="status-field-settings-button personnel-field-settings-button"
                          onClick={() => setShowPersonnelFieldSettings(current => !current)}
                        >
                          Field Settings
                        </button>
                        {showPersonnelFieldSettings ? (
                          <PersonnelFieldSettings
                            columns={personnelFieldColumns}
                            onChangeColumns={handlePersonnelFieldColumnsChange}
                            onClose={() => setShowPersonnelFieldSettings(false)}
                          />
                        ) : null}
                      </div>
                      <FilterToolbar
                        chips={personnelFilterChips}
                        onRemoveChip={clearPersonnelFilter}
                        onClearAll={clearAllPersonnelFilters}
                      />
                    </div>
                  </div>
                  {user?.is_superuser && showMilpersImportModal ? (
                    <div className="milpers-import-modal-backdrop" role="presentation" onMouseDown={() => setShowMilpersImportModal(false)}>
                    <section className="milpers-import-panel" role="dialog" aria-modal="true" aria-label="MILPERS enterprise import" onMouseDown={event => event.stopPropagation()}>
                      <div className="milpers-import-panel__header">
                        <div>
                          <h3>MILPERS Import</h3>
                          <p>Upload one AFRC-wide roster. Rows auto-route to each Personnel table from ASGN_DUTY_LOC; map only unknown duty-location codes or DAFSC shop assignments.</p>
                        </div>
                        <div className="milpers-import-panel__actions">
                          <label className="personnel-upload-button">
                            Import MILPERS
                            <input
                              ref={milpersFileRef}
                              className="personnel-upload-button__input"
                              type="file"
                              accept=".xlsx,.xls,.csv"
                              disabled={isMilpersLoading}
                              onClick={event => { event.target.value = null; }}
                              onChange={handleMilpersUpload}
                            />
                          </label>
                          <button type="button" className="milpers-import-panel__close" onClick={() => setShowMilpersImportModal(false)} title="Close MILPERS import" aria-label="Close MILPERS import">
                            <X size={17} />
                          </button>
                        </div>
                      </div>
                      {isMilpersLoading ? (
                        <div className="milpers-import-panel__notice is-active">
                          <strong>{milpersUploadProgress === null ? 'Processing MILPERS roster' : `MILPERS upload ${milpersUploadProgress}%`}</strong>
                          <span>{milpersProcessingPhase || 'Server is parsing, matching, and applying the roster...'}</span>
                          {milpersImportJob?.fileName ? <small>{milpersImportJob.fileName}</small> : null}
                          {milpersUploadProgress !== null ? (
                            <div className="milpers-import-progress" aria-hidden="true">
                              <i style={{ width: `${Math.max(3, Math.min(100, milpersUploadProgress))}%` }} />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {milpersMessage ? <div className="milpers-import-panel__notice is-success">{milpersMessage}</div> : null}
                      {milpersPartialReviewMessage ? <div className="milpers-import-panel__notice is-warning">{milpersPartialReviewMessage}</div> : null}
                      {milpersError ? <div className="milpers-import-panel__notice is-error">{milpersError}</div> : null}
                      {milpersSummary ? (
                        <div className="milpers-import-summary">
                          <div><strong>Total rows</strong><span>{milpersSummary.totalRows || 0}</span></div>
                          <div><strong>Candidates</strong><span>{milpersSummary.candidateRows || 0}</span></div>
                          <div><strong>Updated</strong><span>{milpersSummary.updated || 0}</span></div>
                          <div><strong>Added</strong><span>{milpersSummary.created || 0}</span></div>
                          <div><strong>Ignored</strong><span>{milpersSummary.ignored || 0}</span></div>
                          <div><strong>Deduped</strong><span>{milpersSummary.deduplicated || 0}</span></div>
                          <div><strong>Blocked</strong><span>{milpersSummary.blocked || 0}</span></div>
                        </div>
                      ) : null}
                      {visibleMilpersImportJobs.length ? (
                        <div className="milpers-import-history">
                          <div className="milpers-import-history__header">
                            <strong>Recent MILPERS Runs</strong>
                            <span>Showing {visibleMilpersImportJobs.length} of {milpersImportJobs.length} saved</span>
                          </div>
                          <div className="milpers-import-history__list">
                            {visibleMilpersImportJobs.map(job => {
                              const summary = job.result?.summary || {};
                              const status = String(job.status || '').toLowerCase();
                              const isCurrent = job.id === selectedMilpersJobId;
                              return (
                                <button
                                  key={job.id}
                                  type="button"
                                  className={[
                                    'milpers-import-history__item',
                                    job.id === selectedMilpersJobId ? 'is-active' : '',
                                    status === 'complete' && Number(summary.blocked || 0) > 0 ? 'is-partial' : '',
                                    status ? `is-${status}` : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={() => handleMilpersHistorySelect(job)}
                                >
                                  <span>
                                    <strong>{job.fileName || 'MILPERS roster'}</strong>
                                    <small>{formatMilpersJobTimestamp(job.completedAt || job.updatedAt || job.createdAt)} | {job.status || 'unknown'}</small>
                                    {isCurrent ? <em>Current</em> : null}
                                  </span>
                                  <span>
                                    <small>{summary.totalRows || 0} rows</small>
                                    <strong>{summary.blocked || 0} blocked</strong>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                      <div className="milpers-import-grid">
                        <div className="milpers-import-workspace">
                          <h4>Duty Location Mapping</h4>
                          {milpersUnmappedDutyLocationRows.length ? (
                            <div className="milpers-import-workspace__chips">
                              {milpersUnmappedDutyLocationRows.map(([code, detail]) => (
                                <button
                                  key={code}
                                  type="button"
                                  onClick={() => setMilpersLocationDraft(prev => ({ ...prev, dutyLocationCode: code === '(blank)' ? '' : code }))}
                                  title={`Rows: ${(detail.rows || []).join(', ')}`}
                                >
                                  {code} ({detail.count})
                                </button>
                              ))}
                            </div>
                          ) : null}
                          <div className="milpers-import-controls">
                            <input
                              value={milpersLocationDraft.dutyLocationCode}
                              onChange={event => setMilpersLocationDraft(prev => ({ ...prev, dutyLocationCode: event.target.value.toUpperCase() }))}
                              placeholder="ASGN_DUTY_LOC"
                            />
                            <select
                              value={milpersLocationDraft.location}
                              onChange={event => setMilpersLocationDraft(prev => ({ ...prev, location: event.target.value }))}
                            >
                              <option value="">Select app location</option>
                              {(milpersMappings.locations || []).map(location => (
                                <option key={location} value={location}>{location}</option>
                              ))}
                            </select>
                            <button type="button" onClick={saveMilpersLocationMapping}>Map Location</button>
                          </div>
                          <div className="milpers-import-list">
                            {(milpersMappings.locationMappings || []).map(mapping => (
                              <div key={mapping.id}>
                                <span><strong>{mapping.dutyLocationCode}</strong> -> {mapping.location}</span>
                                <button type="button" onClick={() => deleteMilpersLocationMapping(mapping.id)} title={`Remove ${mapping.dutyLocationCode}`}>
                                  <X size={15} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="milpers-import-workspace">
                          <h4>DAFSC Shop Mapping</h4>
                          {milpersUnmappedDafscRows.length ? (
                            <div className="milpers-import-workspace__chips">
                              {milpersUnmappedDafscRows.map(([dafsc, detail]) => {
                                const mapped = mappedMilpersDafscCodes.has(normalizeMilpersDafscChip(dafsc));
                                return (
                                  <button
                                    key={dafsc}
                                    type="button"
                                    className={mapped ? 'is-mapped' : ''}
                                    onClick={() => setMilpersDafscSelection(current => Array.from(new Set([...current, dafsc])))}
                                    title={`${mapped ? 'Mapped. ' : ''}Rows: ${(detail.rows || []).join(', ')}`}
                                  >
                                    {dafsc} ({detail.count})
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          <div className="milpers-import-controls">
                            <select
                              multiple
                              value={milpersDafscSelection}
                              onChange={event => setMilpersDafscSelection(Array.from(event.target.selectedOptions, option => option.value))}
                            >
                              {milpersDafscOptions.map(dafsc => (
                                <option key={dafsc} value={dafsc}>{dafsc}</option>
                              ))}
                            </select>
                            <input
                              value={milpersManualDafsc}
                              onChange={event => setMilpersManualDafsc(event.target.value.toUpperCase())}
                              placeholder="Add DAFSC"
                            />
                            <select value={milpersShopName} onChange={event => setMilpersShopName(event.target.value)}>
                              <option value="">Select shop</option>
                              {(milpersMappings.shopOptions || MILITARY_SHOP_OPTIONS).map(shopName => (
                                <option key={shopName} value={shopName}>{shopName}</option>
                              ))}
                            </select>
                            <button type="button" onClick={saveMilpersDafscMapping}>Map DAFSC</button>
                          </div>
                          <div className="milpers-import-list">
                            {(milpersMappings.dafscMappings || []).map(mapping => (
                              <div key={mapping.id}>
                                <span><strong>{mapping.dafsc}</strong> -> {mapping.shopName}</span>
                                <button type="button" onClick={() => deleteMilpersDafscMapping(mapping.id)} title={`Remove ${mapping.dafsc}`}>
                                  <X size={15} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      {milpersSummary?.errors?.length ? (
                        <div className="milpers-import-errors">
                          <h4>Rows Needing Review</h4>
                          {milpersSummary.errors.map(error => (
                            <div key={`${error.row}-${error.dafsc}-${error.fullName}`}>
                              Row {error.row}: {error.fullName || 'No name'} | {error.dutyLocationCode || 'No location code'} | {error.dafsc || 'No DAFSC'} | {(error.errors || []).join('; ')}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {milpersSummary?.deduplicatedRows?.length ? (
                        <div className="milpers-import-errors is-muted">
                          <h4>Duplicate Source Rows Skipped</h4>
                          {milpersSummary.deduplicatedRows.map(row => (
                            <div key={`${row.row}-${row.originalRow}-${row.dafsc}-${row.fullName}`}>
                              Row {row.row}: duplicate of row {row.originalRow} | {row.fullName || 'No name'} | {row.dutyLocationCode || 'No location code'} | {row.dafsc || 'No DAFSC'}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </section>
                    </div>
                  ) : null}
                  {isPersonnelTableRefreshing ? (
                    <div className="personnel-scope-loading" role="status" aria-live="polite">
                      <span className="personnel-scope-loading__spinner" aria-hidden="true" />
                      <div>
                        <strong>Loading Personnel for {personnelRefreshTargetLabel}</strong>
                        {personnelRefreshPreviousLabel ? (
                          <p>Rows shown below may still be from {personnelRefreshPreviousLabel}.</p>
                        ) : (
                          <p>Roster rows are refreshing for the selected site.</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                  <div
                    ref={personnelTopScrollRef}
                    className="personnel-table-top-scroll"
                    onScroll={handlePersonnelTopScroll}
                    hidden={!personnelScrollMetrics.hasOverflow}
                    aria-label="Scroll personnel columns horizontally"
                  >
                    <div
                      className="personnel-table-top-scroll__content"
                      style={{ width: `${personnelScrollMetrics.contentWidth}px` }}
                    />
                  </div>
                  <div
                    ref={personnelTableScrollRef}
                    className={`table-shell personnel-table-shell ${isPersonnelTableRefreshing ? 'is-refreshing' : ''}`}
                    aria-busy={isPersonnelTableRefreshing ? 'true' : 'false'}
                    onScroll={handlePersonnelTableScroll}
                  >
                  {isPersonnelTableRefreshing ? (
                    <div className="personnel-table-refresh-badge" aria-hidden="true">
                      Refreshing {personnelRefreshTargetLabel}
                    </div>
                  ) : null}
                  <table className={`styled-table compact-table personnel-table ${personnelCollapseSettings.collapsed ? 'is-collapsed' : ''}`}>
                      <thead>
                        <tr>
                          {visiblePersonnelColumns.map(key => (
                            <th key={key} className={getPersonnelColumnClass(key)}>
                              <button
                                type="button"
                                onClick={() => handlePersonnelSort(key)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', padding: 0 }}
                              >
                                {getPersonnelColumnLabel(key)}{getSortIndicator(personnelSortConfig, key)}
                              </button>
                            </th>
                          ))}
                          {visiblePersonnelBooleanFields.map(key => (
                            <th key={key} className={getPersonnelColumnClass(key)}>
                              <button
                                type="button"
                                onClick={() => handlePersonnelSort(key)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', padding: 0 }}
                              >
                                {key}{getSortIndicator(personnelSortConfig, key)}
                              </button>
                            </th>
                          ))}
                          {isPersonnelLastUploadedVisible ? (
                            <th className={getPersonnelColumnClass("lastUploadedAt")}>
                              <button
                                type="button"
                                onClick={() => handlePersonnelSort("lastUploadedAt")}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', padding: 0 }}
                              >
                                Last Uploaded{getSortIndicator(personnelSortConfig, "lastUploadedAt")}
                              </button>
                            </th>
                          ) : null}
                          <th>Actions</th>
                        </tr>
                        <tr>
                          {visiblePersonnelColumns.map(key => (
                            <th key={`${key}-filter`} className={getPersonnelColumnClass(key)}>
                              {renderPersonnelFilterCell(key)}
                            </th>
                          ))}
                          {visiblePersonnelBooleanFields.map(key => (
                            <th key={`${key}-filter`} className={getPersonnelColumnClass(key)}>
                              {renderPersonnelFilterCell(key)}
                            </th>
                          ))}
                          {isPersonnelLastUploadedVisible ? (
                            <th className={getPersonnelColumnClass("lastUploadedAt")}>
                              {renderPersonnelFilterCell("lastUploadedAt")}
                            </th>
                          ) : null}
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedPersonnelData.length > 0 ? displayedPersonnelData.map(row => (
                          <tr key={row.id}>
                            {visiblePersonnelColumns.map(key => {
                              const isAccountControlled = Boolean(row._accountLinked && (key === 'Role' || key === 'Shop Name'));
                              const isSmsField = key === 'Mobile Phone' || key === 'SMS Opt-In';
                              const isSmsLocked = Boolean(isSmsField && !personnelSmsEnabled);
                              const hasAccountConflict =
                                (key === 'Role' && row._roleConflict) ||
                                (key === 'Shop Name' && row._shopConflict);
                              const conflictTitle = hasAccountConflict
                                ? `${key} is managed by the Admin panel. Personnel roster value is ${
                                    key === 'Role' ? row._personnelRole || '--' : row._personnelShopName || '--'
                                  }; account master is ${row[key] || '--'}.`
                                : isSmsLocked
                                  ? 'SMS/text notifications are not live yet.'
                                : isAccountControlled
                                  ? `${key} is managed by the Admin panel for registered users.`
                                  : undefined;

                              return (
                              <td
                                key={key}
                                className={[
                                  getPersonnelColumnClass(key),
                                  isAccountControlled ? 'personnel-account-controlled-cell' : '',
                                  isSmsLocked ? 'personnel-premium-locked-cell' : '',
                                  hasAccountConflict ? 'personnel-account-conflict-cell' : '',
                                  key === 'Hours' ? 'personnel-hours-cell' : '',
                                ].filter(Boolean).join(' ')}
                                title={conflictTitle}
                              >
                                {key === 'Labor Category' || key === 'Skill Level' ? (
                                  <select
                                    className="personnel-inline-select"
                                    value={row[key] || ''}
                                    onChange={event => handlePersonnelChange(row.id, key, event.target.value)}
                                    aria-label={`${key} for ${row['Rank'] || ''} ${row['First Name'] || ''} ${row['Last Name'] || ''}`.trim()}
                                  >
                                    <option value="">Select</option>
                                    {(key === 'Labor Category' ? PERSONNEL_LABOR_CATEGORY_OPTIONS : PERSONNEL_SKILL_LEVEL_OPTIONS).map(option => (
                                      <option key={option} value={option}>{option}</option>
                                    ))}
                                  </select>
                                ) : (
                                <EditableCell
                                  value={row[key]}
                                  type={
                                    key === 'Hours'
                                      ? 'time-range'
                                      : key === 'Day Off'
                                      ? 'multi-select'
                                      : (key === 'Rank' || key === 'Role' || key === 'Shop Name' || key === 'Squadron' || key === 'SMS Opt-In')
                                        ? 'select'
                                        : 'text'
                                  }
                                  inputProps={
                                    key === 'Rank'
                                      ? { options: personnelRankOptions }
                                      : key === 'Role'
                                      ? { options: personnelRoleOptions }
                                      : key === 'Squadron'
                                      ? { options: PERSONNEL_SQUADRON_OPTIONS }
                                      : key === 'SMS Opt-In'
                                      ? { options: ['Yes', 'No'] }
                                      : key === 'Day Off'
                                      ? { options: PERSONNEL_DAYS_OFF_OPTIONS, maxSelections: 2, title: 'Days Off' }
                                      : key === 'Shop Name'
                                        ? { options: usafShopOptions }
                                        : {}
                                  }
                                  validateAndNormalize={
                                    key === 'Hours'
                                      ? validatePersonnelHoursValue
                                      : undefined
                                  }
                                  readOnly={isAccountControlled || isSmsLocked}
                                  className={hasAccountConflict ? 'editable-cell--account-conflict' : ''}
                                  emptyLabel={isSmsLocked ? 'Coming soon' : isAccountControlled ? '' : 'Click to add'}
                                  onSave={newVal => handlePersonnelChange(row.id, key, newVal)}
                                />
                                )}
                                {isSmsLocked ? (
                                  <div className="personnel-premium-note">SMS/text not live yet</div>
                                ) : null}
                                {hasAccountConflict ? (
                                  <div className="personnel-account-conflict-note">Admin panel master</div>
                                ) : null}
                              </td>
                            );
                            })}
                            {visiblePersonnelBooleanFields.map(key => (
                              <td key={key} className={getPersonnelColumnClass(key)} style={{ textAlign: 'center' }}>
                                {(() => {
                                  const booleanValue = getPersonnelBooleanValue(row[key]);
                                  const isPresentColumn = key === 'Present?';
                                  const prediction = isPresentColumn && personnelAvailabilityPredictionEnabled
                                    ? buildPersonnelAvailabilityPrediction(row)
                                    : null;
                                  return (
                                    <div className={isPresentColumn ? 'personnel-present-cell' : ''}>
                                      <button
                                        type="button"
                                        className={`toggle-btn ${booleanValue === 'Yes' ? 'yes' : 'no'}`}
                                        onClick={() =>
                                          handlePersonnelChange(
                                            row.id,
                                            key,
                                            booleanValue === 'Yes' ? 'No' : 'Yes'
                                          )
                                        }
                                        style={{
                                          cursor: 'pointer',
                                          padding: '4px 0',
                                        }}
                                      >
                                        {booleanValue}
                                      </button>
                                      {isPresentColumn ? (
                                        <>
                                          <button
                                            type="button"
                                            className={`personnel-present-prediction-button ${prediction ? `is-${prediction.tone}` : 'is-locked'}`}
                                            onClick={event => togglePersonnelAvailabilityPopover(row.id, event)}
                                            title="Show predicted present status"
                                          >
                                            {prediction ? `Predicted ${prediction.predicted}` : 'Prediction'}
                                          </button>
                                          {activePersonnelAvailabilityId === row.id ? (
                                            <div
                                              className="personnel-availability-popover personnel-availability-popover--present"
                                              style={getPersonnelAvailabilityPopoverStyle()}
                                            >
                                              {personnelAvailabilityPredictionEnabled ? (() => {
                                                const activePrediction = prediction || buildPersonnelAvailabilityPrediction(row);
                                                return (
                                                  <>
                                                    <div className="personnel-availability-popover__header">
                                                      <strong>Predicted Present</strong>
                                                      <button type="button" onClick={closePersonnelAvailabilityPopover}>Close</button>
                                                    </div>
                                                    <div className={`personnel-availability-popover__badge is-${activePrediction.tone}`}>
                                                      {activePrediction.predicted}
                                                    </div>
                                                    <dl>
                                                      <div><dt>Manual Present?</dt><dd>{activePrediction.manualPresent ? 'Yes' : 'No'}</dd></div>
                                                      <div><dt>Local Time</dt><dd>{activePrediction.localTime} {activePrediction.todayLabel}</dd></div>
                                                      <div><dt>Hours</dt><dd>{activePrediction.hoursLabel}</dd></div>
                                                      <div><dt>Days Off</dt><dd>{activePrediction.daysOffLabel}</dd></div>
                                                      <div><dt>Last Login</dt><dd>{activePrediction.loginLabel}</dd></div>
                                                    </dl>
                                                    <ul>
                                                      {activePrediction.reasons.map(reason => (
                                                        <li key={reason}>{reason}</li>
                                                      ))}
                                                    </ul>
                                                    <p>Manual Present? remains authoritative for day-of operations.</p>
                                                  </>
                                                );
                                              })() : (
                                                <>
                                                  <div className="personnel-availability-popover__header">
                                                    <strong>Availability Prediction</strong>
                                                    <button type="button" onClick={closePersonnelAvailabilityPopover}>Close</button>
                                                  </div>
                                                  <div className="personnel-availability-popover__badge is-locked">Upgrade</div>
                                                  <p>
                                                    Upgrade to Personnel Availability Prediction to estimate Present? from shift hours,
                                                    days off, and future leave/TDY availability data.
                                                  </p>
                                                </>
                                              )}
                                            </div>
                                          ) : null}
                                        </>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </td>
                              ))}
                              {isPersonnelLastUploadedVisible ? (
                                <td key="uploaded_at" className={getPersonnelColumnClass("lastUploadedAt")}>
                                    {formatLocalTimestamp(row.lastUploadedAt)}
                                </td>
                              ) : null}
                              <td key="delete" className="personnel-actions-cell-wrap" style={{ textAlign: 'center' }}>
                                <button type="button" onClick={() => setPersonnelAvailabilityPerson(row)} title="Manage availability schedule">
                                  <CalendarClock size={17} />
                                </button>
                                <button
                                    type="button"
                                    title="Delete personnel record"
                                    onClick={() => {
                                      if (!window.confirm("Delete this person?")) return;
                                      api.post("/delete-personnel-entry", {
                                        email:    user.email,
                                        location: selectedBase,
                                        id:       row.id
                                      })
                                      .then(() => fetchData("personnel"))
                                      .catch(console.error);
                                    }}
                                   >
                                    <Trash2 size={17} />
                                </button>                            
                              </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={visiblePersonnelColumns.length + visiblePersonnelBooleanFields.length + (isPersonnelLastUploadedVisible ? 1 : 0) + 1} style={{ textAlign: 'center', padding: '12px' }}>
                              {personnelFilterChips.length > 0
                                ? 'No personnel rows match the current filters.'
                                : 'No personnel data available.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {supportUserHasLocations && activeTab === "issuesLog" && (
                <div>
                  <IssuesLog
                      selectedAtoRow={selectedAtoRow}
                      user={user}
                      selectedBase={selectedBase}
                      api={api}
                      issues={issuesData}
                      refreshIssues={fetchIssues}
                      personnelData={personnelData}
                      setAlerts={setAlerts}
                  />
                </div>
              )}
              {supportUserHasLocations && activeTab === 'priorities' && (
                  <PrioritiesTab
                    api={api}
                    user={user}
                    selectedBase={selectedBase}
                    personnelData={personnelData}
                    setAlerts={setAlerts}
                    globalView={globalView}
                  />
              )}
              {supportUserHasLocations && activeTab === 'status' && (
                <StatusTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                  setAlerts={setAlerts}
                  g081ImportsEnabled
                  onOpenFlySchedule={ato => {
                    setSelectedAtoRow(ato);
                    setActiveTab('ato');
                  }}
                />
              )}
              {supportUserHasLocations && canUseCrystalBall && activeTab === 'crystalBall' && (
                <CrystalBallTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'supply' && (
                <SupplyTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'acftPlans' && (
                <ACFTPlansTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'calendar' && (
                <CalendarTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                />
              )}
              {supportUserHasLocations && activeTab === 'weather' && (
                <WeatherTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  latestWeather={weatherSummary}
                  onWeatherUpdate={setWeatherSummary}
                  isEnabled={weatherEnabled}
                  isEntitlementLoading={isHeaderSubscriptionLoading}
                  onOpenUpgrade={() => setActiveTab('upgrade')}
                />
              )}
              {supportUserHasLocations && activeTab === "map" && (
                <MapTab api={api} user={user} selectedBase={selectedBase} baseCoords={baseCoordMap} active />
              )}
              {supportUserHasLocations && activeTab === 'changeLog' && (
                <ChangeLogTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'reports' && (
                <ReportsTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'safety' && (
                <SafetyFormsTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'lostTool' && (
                <LostToolTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  globalView={globalView}
                />
              )}
              {supportUserHasLocations && activeTab === 'admin' && (
                <AdminTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                />
              )}
              {supportUserHasLocations && activeTab === 'ownerPortal' && (
                <MFEPlatformOwnerPortal
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                />
              )}
              {supportUserHasLocations && activeTab === 'upgrade' && (
                <UpgradeTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  billingReturn={billingReturn}
                />
              )}
              {supportUserHasLocations && activeTab === 'help' && (
                <HelpTab
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  onReplayTutorial={() => {
                    setOnboardingStep(0);
                    setOnboardingDontShowAgain(false);
                    setShowOnboarding(true);
                  }}
                />
              )}
              {personnelAvailabilityPerson ? (
                <PersonnelAvailabilityModal
                  api={api}
                  user={user}
                  selectedBase={selectedBase}
                  person={personnelAvailabilityPerson}
                  onClose={() => setPersonnelAvailabilityPerson(null)}
                />
              ) : null}
            </main>
          </div>
        )
        )
    );
}

export default App;
